import { createContext, Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { QueryClient, UseQueryResult } from "@tanstack/react-query";
import { useIsMutating, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import MediaStrip from "@/components/MediaStrip";
import { Toast, useToast } from "@/components/Toast";
import { useT } from "@/i18n";
import { failText } from "@/lib/failText";
import { fmin, stampAt, todayHere, todayInZurich, useFormat, WarehouseZone, zoneOf } from "@/lib/format";
import { instant, useNow } from "@/lib/clock";
import { ReadOnlyArea, ReadOnlyBanner, useReadOnly } from "@/components/ReadOnly";
import { PastBoard } from "@/pages/SupervisorPage";
import {
  fetchDelivery, fetchFitters, startRun, resumeRun, pauseRun, setQuantity, addFitters, removeFitter,
  reportIssue, finishRun, reopenRun, sendRecap, addFitterName, resetRun, undoResetRun,
  archiveJob, fetchJobs, fetchProgress, fetchGuide,
  type JobsResponse, type JobProgress, type MaybeItem, type AssemblyJob, type CitedOrder, type SupplierDocument, type Addressees, type JobTab, type Delivery, type Item, type Pack, type ClientPreparation, type Issue,
  type GuideVideo, type QtyChange, type Run, type Fitters,
  mediaRun,
} from "@/services/preparation";
import { fetchReceptionForEvent } from "@/services/receiving";
import { fetchMedia, type MediaFile } from "@/services/media";
import { ReceiptHead, ReceptionResultView } from "@/components/ReceptionResultView";

const ISSUE_KINDS = [
  "damaged_furniture", "missing_part", "missing_furniture",
  "wrong_model_or_colour", "missing_instructions", "other",
] as const;

// The open job's client and number: his tablet request label "client — what" on a line's photos, and
// the "#number client" line of its photo sheet; its planning entry and the list shown, for a tablet
// asked before the job has its run.
const JobClient = createContext<{ client: string; number: number | null; eventId: number | null; fromOrders: boolean }>({ client: "", number: null, eventId: null, fromOrders: false });
// Makes the job's run for photos taken before the start (his photos exist from the first moment).
const EnsureRun = createContext<(() => Promise<number>) | null>(null);

export default function PreparationPage({ warehouseId, jobs, initialEvent = null }: {
  warehouseId: number | null;
  jobs: UseQueryResult<JobsResponse, Error>;
  initialEvent?: number | null;
}) {
  const { t, locale } = useT();
  const { zurichDayMonth, zurichTime } = useFormat();
  const [toastMsg, toast] = useToast();
  const [selected, setSelected] = useState<number | null>(initialEvent);
  const [tab, setTab] = useState<JobTab>("today");
  // His fifth Montage tab, "Réel vs estimé", open to every fitter.
  const [stats, setStats] = useState(false);
  const [search, setSearch] = useState("");
  // Ludovic's filter, on by default: only his assembly jobs. Off, the whole planning of the same days.
  const [ludovic, setLudovic] = useState(true);
  const everything = useQuery({ queryKey: ["prep-jobs", false], queryFn: () => fetchJobs(false), enabled: !ludovic, refetchInterval: 30_000,
    gcTime: KEEP_READS });
  const source = ludovic ? jobs : everything;
  // Every manual archive choice the lists show, for the tab a later pause or reopen goes to (`place`).
  useEffect(() => { [jobs.data, everything.data].forEach((d) => d?.jobs.forEach(rememberManual)); }, [jobs.data, everything.data]);

  const mine = useMemo(
    () => (source.data?.jobs ?? []).filter((j) => warehouseId === null || j.warehouse?.id === warehouseId),
    [source.data, warehouseId]
  );
  const today = source.data?.today ?? "";
  // His tabs: Today also keeps the jobs finished today; Upcoming leaves out a job taken off the planning.
  const inTab = (j: AssemblyJob, k: JobTab) =>
    k === "today" && j.tab === "done" ? planningDay(j.run?.finished_at) === today
      : k === "upcoming" ? j.tab === k && !j.gone : j.tab === k;
  const count = (k: JobTab) => mine.filter((j) => inTab(j, k)).length;
  const needle = search.trim().toLowerCase();
  // His order: Today running → paused → to do → finished, then by day and client; Upcoming by day under
  // day headings; Finished newest finish first, 80 at most.
  const rank = (j: AssemblyJob) => ({ running: 0, paused: 1, finished: 3 } as Record<string, number>)[j.run?.status ?? ""] ?? 2;
  const byDayClient = (a: AssemblyJob, b: AssemblyJob) => a.day.localeCompare(b.day) || (a.client ?? "").localeCompare(b.client ?? "");
  // His Archived search: the client's name (accents ignored), or the digits of the event number or id.
  const matches = (text: string) => {
    const n = text.trim().toLowerCase(), digits = text.replace(/\D/g, "");
    return (j: AssemblyJob) => !n || plain(j.client ?? "").includes(plain(n))
      || (!!digits && (String(j.number ?? "").includes(digits) || String(j.id).includes(digits)));
  };
  const listOf = (found: (j: AssemblyJob) => boolean) => mine.filter((j) => inTab(j, tab)).filter((j) => tab !== "archived" || found(j))
    .sort(tab === "today" ? (a, b) => rank(a) - rank(b) || byDayClient(a, b)
      : tab === "done" ? (a, b) => (b.run?.finished_at ?? "").localeCompare(a.run?.finished_at ?? "")
      : tab === "archived" ? (a, b) => archivedOn(b).localeCompare(archivedOn(a)) || (a.client ?? "").localeCompare(b.client ?? "")
      : byDayClient)
    .slice(0, tab === "done" ? 80 : tab === "archived" ? ARCHIVED_SHOWN : undefined);
  const found = matches(search);
  const listed = mine.filter((j) => inTab(j, tab)).filter((j) => tab !== "archived" || found(j));
  const shown = listOf(found);
  // The search filters at each key, as his; the cards' numbers are read again only once typing stops,
  // so a word typed is not a read per letter.
  const settled = useSettled(search, 400);
  // PRÉPARATION CLIENT cards: today's under Today, later ones under Upcoming.
  const preps = (source.data?.preparations ?? []).filter((p) => (warehouseId === null || p.warehouse?.id === warehouseId)
    && (tab === "today" ? p.starts_at.slice(0, 10) <= today : tab === "upcoming" ? p.starts_at.slice(0, 10) > today : false));
  // The open job's card, for its sheet head: the list shown, else any list held — a job another tab opened
  // here (Reception's "Open the job", the supervisor's) may sit in another warehouse or outside Ludovic's
  // filter. One in no list draws its head from its own sheet (`sheetJob`).
  const selectedJob = mine.find((j) => j.id === selected)
    ?? [source.data, jobs.data, everything.data].flatMap((d) => d?.jobs ?? []).find((j) => j.id === selected) ?? null;
  // His card numbers for every card shown — a planning entry not started included, as his every card
  // carries its estimate, counts and bar. The list brings them, worked out ahead (`job.progress`), so a
  // card paints with them; they are then read again for every card shown, at the list's own pace, a few
  // jobs a call so no read is long, in batches keyed by the SET of cards shown, never their order: a
  // card moving to another group keeps its batch.
  const progressKey = (settled === search ? shown : listOf(matches(settled))).map((j) => j.id).sort((a, b) => a - b).join(",");
  const batches = useMemo(() => {
    const ids = progressKey ? progressKey.split(",").map(Number) : [];
    return Array.from({ length: Math.ceil(ids.length / PROGRESS_BATCH) }, (_, i) => ids.slice(i * PROGRESS_BATCH, (i + 1) * PROGRESS_BATCH));
  }, [progressKey]);
  useQueries({
    queries: batches.map((ids) => ({
      queryKey: ["job-progress", ids.join(",")], queryFn: () => fetchProgress(ids),
      staleTime: 30_000, gcTime: KEEP_READS, refetchInterval: tab === "archived" ? 120_000 : 30_000,
    })),
  });
  const qc = useQueryClient();
  // His filter, as his chips: ticking "Assembly jobs only" closes an open job that is not one of them.
  const lastFilter = useRef(ludovic);
  useEffect(() => {
    if (lastFilter.current === ludovic) return;
    lastFilter.current = ludovic;
    if (selected !== null && jobs.data && !jobs.data.jobs.some((j) => j.id === selected)) setSelected(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ludovic]);
  // His chips: picking another warehouse closes a job that is not in it — a pick, not the page opening
  // on a job another tab sent here.
  const lastWarehouse = useRef(warehouseId);
  useEffect(() => {
    if (lastWarehouse.current === warehouseId) return;
    lastWarehouse.current = warehouseId;
    if (warehouseId === null || selected === null) return;
    const open = (source.data?.jobs ?? []).find((j) => j.id === selected);
    if (open && open.warehouse?.id !== warehouseId) setSelected(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId]);
  // His narrow screen: the open job takes the whole screen and opens at its top.
  const toTopIfNarrow = () => { if (window.innerWidth < WIDE) window.scrollTo({ top: 0 }); };

  const tabRow = (
    <div className="mb-3 flex flex-wrap gap-1">
      {(["today", "upcoming", "done", "archived"] as const).map((k) => (
        <button key={k} onClick={() => { setTab(k); setStats(false); }}
          className={`rounded-lg px-2.5 py-1 text-sm ${tab === k && !stats ? "bg-slate-900 text-white" : "text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
          {t(`jobs.${k}`)} <span className={tab === k && !stats ? "text-slate-300" : "text-slate-400"}>{count(k)}</span>
        </button>
      ))}
      <button onClick={() => setStats(true)}
        className={`rounded-lg px-2.5 py-1 text-sm ${stats ? "bg-slate-900 text-white" : "text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
        {t("hist.past")}
      </button>
    </div>
  );

  // His Montage header, on every Montage view: "Z Furniture montage", the sync line, the read-only
  // banner, then the guide.
  const head = (
    <>
      <div className="mb-3">
        <h2 className="text-lg font-semibold tracking-tight text-slate-900">{t("brand.z")} {t("brand.furniture")} <span className="text-orange-600">{t("jobs.titleMode")}</span></h2>
        {/* His sync line. Our list is read from the planning live, so this is when it was read — empty
            while the planning loads or failed (its error shows below), as his before his base answers. */}
        <p className="min-h-[1rem] text-xs text-slate-500">
          {source.data ? t("jobs.readAt", { d: zurichDayMonth(source.dataUpdatedAt), t: zurichTime(source.dataUpdatedAt) }) : ""}
        </p>
      </div>
      <ReadOnlyBanner module="preparation" text={t("job.readOnly")} />
      {source.error && <Problem message={(source.error as Error).message} />}
      <GuidePanel />
    </>
  );

  // His one `#toast`, for the page and every job opened on it: a write that fails after the person went
  // back to the list is still said, and a later message replaces an earlier one. Last, so it shows over
  // an open sheet.
  const toastLine = <Toast message={toastMsg} />;

  if (stats) return <div>{head}{tabRow}<PastBoard warehouseId={warehouseId} />{toastLine}</div>;

  return (
    <div>
      {head}
      {/* Side by side from 980 px, as his; with no job open the list takes the whole width. */}
      {/* The columns may shrink below their content: on a phone nothing pushes the page sideways. */}
      <div className={`grid grid-cols-[minmax(0,1fr)] gap-6 ${selected ? "min-[980px]:grid-cols-[23rem_minmax(0,1fr)]" : ""}`}>
        {/* On a phone the open job takes the screen, as his; "← List" brings the list back. */}
        <section className={`min-w-0 ${selected ? "hidden min-[980px]:block" : ""}`}>
          <label className="mb-2 flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={ludovic} onChange={(e) => setLudovic(e.target.checked)} />
            {t("jobs.onlyAssembly")}
          </label>
          {/* His Montage tabs. */}
          {tabRow}
          {tab === "archived" && (
            <>
              <h3 className="mb-1 text-sm font-semibold text-slate-800">{t("arch.t")}</h3>
              <p className="mb-2 text-xs text-slate-500">{t("arch.hint")}</p>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("arch.q")}
                className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
            </>
          )}
          {source.isPending && <p className="text-sm text-slate-400">{t("loading")}</p>}
          {tab === "archived" && listed.length > 0 && (
            <p className="mb-2 text-xs text-slate-500">{t(listed.length > 1 ? "arch.nMany" : "arch.nOne", { n: listed.length })}</p>
          )}
          <ul className="space-y-1.5">
            {preps.map((p) => <ClientPrepCard key={`prep-${p.reservation_id}`} prep={p} />)}
            {shown.map((j, i) => (
              <Fragment key={j.id}>
                {tab === "upcoming" && j.day !== shown[i - 1]?.day && <li className="pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{longDay(j.day, locale)}</li>}
                <JobCard job={j} active={selected === j.id} onSelect={(id) => { setSelected(id); toTopIfNarrow(); }}
                  showWarehouse={warehouseId === null} progress={progressOf(qc, j, source.dataUpdatedAt)} />
                {tab === "archived" && <ArchivedReason job={j} onDone={toast} />}
              </Fragment>
            ))}
          </ul>
          {tab === "archived" && listed.length > shown.length && (
            <p className="mt-2 text-xs text-slate-500">{t("arch.more", { n: listed.length - shown.length })}</p>
          )}
          {source.data && shown.length === 0 && preps.length === 0 && (
            tab === "archived" ? <p className="text-sm text-slate-400">{needle ? t("arch.noHit") : t("arch.none")}</p> : (
              <div className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm">
                <b className="block text-slate-700">{t(EMPTY[tab][0])}</b>
                <span className="text-slate-500">{t(EMPTY[tab][1])}</span>
              </div>
            )
          )}
        </section>

        {selected != null && (
          <section className="min-w-0">
            {/* Keyed by job: each job opens on its own list, never on the one the previous job showed. */}
            <Job key={selected} eventId={selected} summary={selectedJob} listAt={source.dataUpdatedAt} onToast={toast}
              onClose={() => { setSelected(null); toTopIfNarrow(); }} />
          </section>
        )}
      </div>
      {toastLine}
    </div>
  );
}

// His layout switches at 980 px: list and job side by side above, one at a time below.
const WIDE = 980;
// Jobs per progress read: a few, so no read comes near the 30 s a request may take.
const PROGRESS_BATCH = 10;
// How long the lists and the card numbers stay held once off screen (refreshed every 30 s to 2 min):
// back on the page an hour later, every card paints with what it last showed.
const KEEP_READS = 60 * 60_000;

// An answer worked out while the job was finished carries his 100 % (JobProgress forces it); any other
// carries the work really done — running, paused and to-do alike. The card's own status says which it
// needs, as his `pp` reads the live status. An answer that does not say its status fits either.
function fits(p: JobProgress, status: string | undefined) {
  return p.status === undefined || (p.status === "finished") === (status === "finished");
}

// Every answer already held for a job — the list's own (read at `listAt`), each batch's, its sheet's, a
// write's, a tap's — the newest one worked out for the card's status first (`fits`), else the newest of
// any: a read not back yet, or one the server has not worked out (null), never blanks an estimate, a count
// or a bar. The card and the sheet head both pick this way, as his card and head read one `j`.
function progressOf(qc: QueryClient, j: AssemblyJob, listAt: number) {
  let best: { at: number; p: JobProgress; fit: boolean } | undefined;
  const weigh = (at: number, p: JobProgress | null | undefined) => {
    if (!p) return;
    const fit = fits(p, j.run?.status);
    if (!best || (fit && !best.fit) || (fit === best.fit && at > best.at)) best = { at, p, fit };
  };
  weigh(listAt, j.progress);
  for (const c of qc.getQueryCache().findAll({ queryKey: ["job-progress"] })) {
    weigh(c.state.dataUpdatedAt, (c.state.data as { progress?: Record<string, JobProgress | null> } | undefined)?.progress?.[String(j.id)]);
  }
  return best?.p;
}

// His search waits for nothing; what it reads again waits for the typing to stop.
function useSettled<T>(value: T, ms: number) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return settled;
}

// One of his assembly jobs (`card`): the number and client, his pills, the assembly and delivery days,
// the estimate and the time worked, the counts, and the progress bar.
function JobCard({ job, active, onSelect, showWarehouse, progress: read }: {
  job: AssemblyJob; active: boolean; onSelect: (id: number) => void; showWarehouse: boolean; progress: JobProgress | undefined;
}) {
  const progress = read ?? null;
  const { t } = useT();
  const { dayLbl } = useFormat();
  const seconds = useLiveSeconds(job.run);
  const worked = seconds == null ? null : seconds / 60;
  // A planning entry says whether sales and logistics approved it; a job kept from its run alone says nothing.
  const waiting = job.sales_approved === false ? t("event.awaitingSales") : job.logistics_approved === false ? t("event.awaitingLogistics") : null;
  // A job a manager reset reads as not started.
  const status = job.run?.status === "idle" ? undefined : job.run?.status;
  const muted = active ? "text-slate-300" : "text-slate-500";
  const strong = active ? "text-white" : "text-slate-800";
  // His card reads the sheet's own estimate (`a.est`): the job's progress answer carries the one its head
  // shows, the list's is the planning entry's, and a job the list has none for (not one of his entries)
  // has its run's copy. Nothing is written until one of them is known — never a "0 min" for a job that
  // has an estimate; once its numbers are read and carry none, his `a.est || 0`.
  const est = progress?.estimate_minutes ?? job.estimate_minutes ?? job.run?.estimated_minutes ?? (progress ? 0 : null);
  // His bar (`pp`, `tp`): the work done — 100 the moment the card says finished, and never the finished
  // 100 once it says anything else (`fits`) — and a mark at the share of the estimate already spent, from
  // the exact person-seconds, as his tm.pers, never from minutes already rounded. A card whose numbers
  // are not read yet draws his bar at 0 %.
  const workPct = status === "finished" ? 100 : progress && fits(progress, status) ? progress.work_pct : 0;
  const spentPct = status && est && seconds != null ? Math.min(100, Math.round((seconds / (est * 60)) * 100)) : 0;
  return (
    <li>
      <button onClick={() => onSelect(job.id)}
        className={`w-full rounded-lg border px-3 py-2 text-left transition ${
          active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}>
        <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
          <span className="min-w-0 truncate text-sm font-medium">
            {job.number != null && <span className="text-slate-400">#{job.number} </span>}{job.client}
          </span>
          <span className="flex flex-wrap gap-1 text-xs"><Pills job={job} status={status} progress={progress} /></span>
        </div>
        <div className={`mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs ${muted}`}>
          <span>{t("jobs.asm")} <b className={strong}>{dayLbl(job.day)}</b></span>
          {job.assembly || job.delivery_on !== job.day
            ? <span>{job.delivery_on ? <>{t("jobs.deliv")} <b className={strong}>{dayLbl(job.delivery_on)}</b></> : <i>{t("jobs.noDelivery")}</i>}</span>
            : <span>{job.type_label}</span>}
          {showWarehouse && job.warehouse && <span>{job.warehouse.name}</span>}
          {est != null
            ? <span>{t("jobs.est")} <b className={strong}>{fmin(est)}</b>{status && worked != null ? <> · <b className={strong}>{fmin(worked)}</b></> : null}</span>
            : status && worked != null && <b className={strong}>{fmin(worked)}</b>}
          {/* His `naCount` is 0 once finished, whatever an earlier read said. */}
          {progress && status !== "finished" && progress.not_arrived > 0 && (
            <span className="rounded bg-red-600 px-1.5 font-semibold text-white motion-safe:animate-pulse">
              {t(progress.not_arrived > 1 ? "jobs.notArrivedMany" : "jobs.notArrivedOne", { n: progress.not_arrived })}
            </span>
          )}
          {progress && progress.assembled.total > 0 && <span><b className={strong}>{progress.assembled.done}/{progress.assembled.total}</b> {t("job.assembled")}</span>}
          {progress && progress.checked.total > 0 && <span><b className={strong}>{progress.checked.done}/{progress.checked.total}</b> {t("job.checked")}</span>}
          {(status === "running" || status === "paused") && (job.run?.team?.length ?? 0) > 0 && <span>👷 {job.run!.team!.join(", ")}</span>}
          {job.gone && <span>{t("jobs.gone")}</span>}
          {waiting && <Badge tone="amber">{waiting}</Badge>}
        </div>
        <div className="mt-1.5 flex items-center gap-2.5" title={spentPct > 0 ? t("jobs.progressT", { p: workPct, t: spentPct }) : t("jobs.progress", { p: workPct })}>
          <span className={`relative h-1.5 flex-1 rounded ${active ? "bg-slate-700" : "bg-slate-200"}`} aria-hidden="true">
            <i className="block h-full max-w-full rounded bg-emerald-500" style={{ width: `${workPct}%` }} />
            {spentPct > 0 && <b className={`absolute -bottom-[3px] -top-[3px] -ml-px w-0.5 rounded-sm ${active ? "bg-slate-300" : "bg-slate-600"}`} style={{ left: `${spentPct}%` }} />}
          </span>
          <b className="min-w-[5ch] text-right font-mono text-xs">{workPct} %</b>
        </div>
      </button>
    </li>
  );
}

// His `pills` (index.html:704): the status (to do · overdue), the issues reported, the share received
// (not once finished), then the reception pill — nothing else. An archived job says so by its tab, its
// reason line and the sheet's "📦 Archived client" box, as his. "Overdue" is read on this device's own
// day, as his `j.a.day<today()` — a Hackbridge tablet at 23:30 on the job's day still reads "To do".
function Pills({ job, status, progress }: { job: AssemblyJob; status: string | undefined; progress: JobProgress | null }) {
  const { t } = useT();
  const today = todayHere();
  const rcv = progress && status !== "finished" && progress.received.total > 0 ? progress.received : null;
  const rcvPct = rcv ? (rcv.got >= rcv.total ? 100 : Math.min(99, Math.round((rcv.got / rcv.total) * 100))) : 0;
  const rx = progress?.reception;
  return (
    <>
      {status === "running" && <Badge tone="emerald">{t("job.inProgress")}</Badge>}
      {status === "paused" && <Badge tone="amber">{t("run.paused")}</Badge>}
      {status === "finished" && <Badge tone="emerald">{t("job.finished")}</Badge>}
      {/* His "À faire · en retard": not started and its assembly day has passed. */}
      {!status && (!!job.day && job.day < today && !isArch(job) ? <Badge tone="slate">{t("job.todo")} · {t("jobs.overdue")}</Badge> : <Badge tone="slate">{t("job.todo")}</Badge>)}
      {job.issues > 0 && <Badge tone="red">{job.issues > 1 ? t("jobs.issues", { n: job.issues }) : t("jobs.issue")}</Badge>}
      {rcv && (
        <Badge tone={rcvPct >= 100 ? "emerald" : "amber"} title={t(rcv.got > 1 ? "jobs.receivedOfMany" : "jobs.receivedOfOne", { g: rcv.got, n: rcv.total })}>
          {t("jobs.receivedPct", { p: rcvPct })}
        </Badge>
      )}
      {rx === "warn" && <Badge tone="red">{t("jobs.rxWarn")}</Badge>}
      {rx === "ok" && <Badge tone="emerald">{t("jobs.rxOk")}</Badge>}
      {rx === "confirmed" && <Badge tone="amber">{t("jobs.rxConfirmed")}</Badge>}
    </>
  );
}

// His `isArch`: never while the job runs (index.html:667) — whatever a list says, the overdue rule and the
// sheet's archive boxes follow the job's status at once.
function isArch(j: AssemblyJob) {
  return j.archive.archived && j.run?.status !== "running";
}

// His card time (`times(j.r, now)`), in exact person-seconds: what the server counted as of `as_of`,
// carried on to the shared `now` — the timer's own instant, never held at `as_of`, so the card and the
// timer read the same minute on every tick and a refresh lands on the very value it replaces.
function useLiveSeconds(run: AssemblyJob["run"]) {
  const people = run?.running_people ?? 0;
  const now = useNow(people > 0);
  // The time a stopped clock showed: an answer starting it a moment after this screen's last tick holds
  // there, as his timer holds a segment not begun yet at 0 — the card never dips below it at a start.
  const stopped = useRef<{ id: number; s: number } | null>(null);
  if (!run || (run.worked_seconds == null && run.worked_minutes == null)) return null;
  const since = run.as_of ? (now - Date.parse(run.as_of)) / 1000 : 0;
  let s = Math.max(0, (run.worked_seconds ?? (run.worked_minutes ?? 0) * 60) + since * people);
  if (!people) stopped.current = { id: run.id, s };
  else if (stopped.current?.id === run.id) s = Math.max(s, stopped.current.s);
  return s;
}

// His Archived list: newest archive first — the day it was archived by hand, else its delivery — 100 at most.
const ARCHIVED_SHOWN = 100;
function archivedOn(j: AssemblyJob) {
  return j.archive.how === "manual" && j.archive.archived ? planningDay(j.archive.at) : j.delivery_on || j.day;
}
// His `nk`: lower case, accents ignored.
function plain(s: string) {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

// Under each archived card: why it is there, and his "Unarchive" (a write: greyed for read-only).
function ArchivedReason({ job, onDone }: { job: AssemblyJob; onDone: (msg: string) => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const readOnly = useReadOnly("preparation");
  const unarchive = useMutation({
    mutationFn: () => archiveJob(job.id, false),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["prep-jobs"] }); onDone(t("arch.undone", { c: job.client ?? "—" })); },
  });
  const day = archivedOn(job);
  return (
    <li className="-mt-1 rounded-b-lg px-3 pb-1 text-xs text-slate-500">
      <div className="flex items-center justify-between gap-2">
        <span>{t(job.archive.how === "manual" ? "arch.man" : "arch.auto", { d: day ? `${day.slice(8, 10)}.${day.slice(5, 7)}` : "—" })}</span>
        <button onClick={() => unarchive.mutate()} disabled={unarchive.isPending || readOnly}
          className="rounded-lg border border-slate-300 bg-white px-2.5 py-0.5 text-xs text-slate-700 disabled:opacity-40">{t("arch.un")}</button>
      </div>
      {unarchive.error && <p role="alert" className="mt-1 text-red-700">{failText(t, unarchive.error, "job.writeFailed")}</p>}
    </li>
  );
}

// His empty tabs: what is missing, then where to look.
const EMPTY = {
  today: ["job.emptyToday", "job.emptyTodayHint"],
  upcoming: ["job.emptyUpcoming", "job.emptyUpcomingHint"],
  done: ["job.emptyDone", "job.emptyDoneHint"],
} as const;

// The calendar day a job finished on, in the server's zone (config.time_zone) — the one "today" comes from.
function planningDay(iso?: string | null) {
  return iso ? new Date(iso).toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" }) : "";
}

// "Monday 03.11", his day heading in Upcoming.
function longDay(iso: string, locale: string) {
  const tag = { fr: "fr-CH", en: "en-GB", de: "de-CH", it: "it-CH" }[locale] ?? "fr-CH";
  return new Date(`${iso}T12:00:00`).toLocaleDateString(tag, { weekday: "long", day: "2-digit", month: "2-digit" });
}

function Badge({ tone, title, children }: { tone: "amber" | "emerald" | "slate" | "red"; title?: string; children: React.ReactNode }) {
  const tones = {
    amber: "bg-amber-100 text-amber-800", emerald: "bg-emerald-100 text-emerald-800",
    slate: "bg-slate-200 text-slate-700", red: "bg-red-100 text-red-800",
  };
  return <span title={title} className={`rounded px-1.5 py-0.5 font-medium ${tones[tone]}`}>{children}</span>;
}

type ListRun = NonNullable<AssemblyJob["run"]>;
// His `times` at this instant, kept on a card's run when the screen changes it before the server answers:
// the exact seconds so far, dated on the screen's own clock, from now on counted for `people` — a pause
// neither loses nor invents a second. A tap never counts faster than the server has said (it dates the
// new segment itself, a moment later): it may only stop the clock or slow it until the answer.
function settle(run: ListRun, people: number, change: Partial<ListRun>): ListRun {
  const now = instant();
  const since = run.as_of ? (now - Date.parse(run.as_of)) / 1000 : 0;
  const secs = Math.max(0, (run.worked_seconds ?? (run.worked_minutes ?? 0) * 60) + since * (run.running_people ?? 0));
  return { ...run, ...change, worked_seconds: secs, worked_minutes: Math.round(secs / 60), as_of: new Date(now).toISOString(),
    running_people: Math.min(people, run.running_people ?? 0) };
}
// His `arcOver` sits beside the job (his `archive` collection) even while it runs. The list says it in
// `archive.held`; a list without it says nothing about a running job (never archived then), so each manual
// choice a list showed is also kept here by the moment it was made (`archive.at`, sent either way) for
// the tab a later pause goes to.
const MANUAL = new Map<number, { at: string | null; archived: boolean }>();
const heldOf = (j: AssemblyJob) => j.archive.held ?? (j.run?.status === "running" ? null : j.archive);
function rememberManual(j: AssemblyJob) {
  const h = heldOf(j);
  if (h?.how === "manual") MANUAL.set(j.id, { at: h.at, archived: h.archived });
  else if (h) MANUAL.delete(j.id);
}
// His `tabOf` with `isArch`, `arcOver` and `arcAuto`, on the list's own day (`today`, the warehouse clock
// the server lists by): a running job is under Today; a manual choice holds; otherwise archived the day
// after its delivery, except on the day it was finished; then Finished, Upcoming (not started, a later
// day) or Today. Every change the screen makes to a card's run moves it here, at the tap.
function place(j: AssemblyJob, today: string): AssemblyJob {
  if (!today) return j;
  const st = j.run?.status;
  const kept = MANUAL.get(j.id);
  const h = j.archive.held;
  const held = h ? (h.how === "manual" ? h.archived : null)
    : j.archive.how === "manual" ? j.archive.archived : kept && kept.at === j.archive.at ? kept.archived : null;
  const auto = !!j.delivery_on && j.delivery_on < today && !(st === "finished" && planningDay(j.run?.finished_at) === today);
  const archived = st !== "running" && (held ?? auto);
  const tab: JobTab = archived ? "archived" : st === "finished" ? "done" : j.day > today && (!st || st === "idle") ? "upcoming" : "today";
  return { ...j, tab, archive: { ...j.archive, archived, how: st === "running" ? null : held != null ? "manual" : archived ? "auto" : null } };
}

// His bar (`pp`) as the server counts it (JobProgress.figures), finished or not: each piece weighted by
// its minutes (1 when it has none), done when assembled — or, for a piece only to check, checked.
function workDone(items: Item[]) {
  let all = 0, done = 0;
  for (const i of items) {
    const w = (i.estimated_minutes_per_piece ?? 0) > 0 ? i.estimated_minutes_per_piece! : 1;
    all += w * i.qty;
    done += w * Math.min(i.qty, i.needs_assembly ? i.assembled_qty : i.checked_qty);
  }
  return all > 0 ? Math.floor((done / all) * 100) : 0;
}

// The card's run as a write's answer has it: card and timer then count from one answer.
function fromAnswer(run: ListRun | null, r: Run): ListRun {
  return { ...(run ?? {}), id: r.id, status: r.status, number: r.number, team: r.team, finished_at: r.finished_at, furniture_list: r.furniture_list,
    ...(r.as_of ? { worked_seconds: r.worked_seconds ?? r.worked_minutes * 60, worked_minutes: r.worked_minutes, as_of: r.as_of, running_people: r.running_people ?? 0 } : {}) };
}

// His `claim` on the job a fitter was taken off: the names leave its team; a running clock closes its
// segment and opens one for whoever is left, or the job pauses when nobody is.
function leaveRun(run: Run, names: string[], status: string | undefined, at: string): Run {
  const team = run.team.filter((n) => !names.includes(n));
  const next = status ?? (run.status === "running" && !team.length ? "paused" : run.status);
  if (run.status !== "running") return { ...run, team, status: next };
  const closed = run.segments.map((g) => (g.ended_at ? g : { ...g, ended_at: at }));
  return { ...run, team, status: next, segments: next === "running" && team.length ? [...closed, { id: 0, started_at: at, ended_at: null, worker_names: team }] : closed };
}

// His finish screen comes once per job and page session: finishing again after a reopen does not
// thank the team a second time.
const THANKED = new Set<number>();

export function Job({ eventId, summary = null, listAt = 0, onClose, onToast }: {
  eventId: number; summary?: AssemblyJob | null; onClose?: () => void; onToast: (msg: string) => void;
  // When the list `summary` comes from was read: its card figures are dated by it, as on the card.
  listAt?: number;
}) {
  const { t, locale } = useT();
  const { dayLbl } = useFormat();
  // His one toast is the page's: what a job says outlives the job closing.
  const toast = onToast;
  // His `openThanks(id, sentTo)`: the answer that finished the job, and the rep its summary went to ("" when none).
  const [thanks, setThanks] = useState<{ data: Delivery; sentTo: string } | null>(null);
  const qc = useQueryClient();
  // "restart": the start picker after a refused Resume, opened with nobody picked.
  const [dialog, setDialog] = useState<null | "start" | "restart" | "addFitter" | "finish" | "resend" | { issueFor: number | null }>(null);
  // His Finish sheet's SH.done and its #sMsg: the job finished by this sheet while its summary could not go
  // out — the sheet stays open with the error, and its next tap only sends.
  const [fin, setFin] = useState<{ done: boolean; error: string | null }>({ done: false, error: null });
  const openFin = (kind: "finish" | "resend") => { setFin({ done: false, error: null }); setDialog(kind); };
  // Which furniture list to show: Ludovic's (the supplier orders the entry cites) or the entry's whole
  // Delivery Manager list. A started job opens on the list it is ticked in; an assembly job not
  // started yet opens on his. null = let the server answer with the job's own list.
  const [fromOrders, setFromOrders] = useState<boolean | null>(
    summary ? (summary.run ? summary.run.furniture_list === "orders" : summary.assembly) : null);

  // His live job (onSnapshot): another fitter's ticks, issues and run changes appear within 15 s (each read asks the feed once). Never
  // while this device is saving a tap — the answer of that tap is the newer truth.
  const saving = useIsMutating() > 0;
  const q = useQuery({ queryKey: ["prep-event", eventId, fromOrders], queryFn: () => fetchDelivery(eventId, fromOrders), refetchInterval: saving ? false : 15_000 });
  // His `rxMark`: what each line shows as received on the notes that prove arrival — the most any one
  // note says, by its order and title (his `gkey`), the same query as the job's reception block.
  const notes = useQuery({ queryKey: ["reception-for-event", eventId], queryFn: () => fetchReceptionForEvent(eventId) });
  // His `mediaOf(run, gkey)`: the job's files by furniture line (the same list the finish counts).
  const runId = q.data?.run?.id ?? null;
  const runMedia = useQuery({ queryKey: ["media", "run", runId], queryFn: () => fetchMedia("run", runId!), enabled: runId != null });
  const lineMedia = useMemo(() => {
    const by: Record<string, MediaFile[]> = {};
    for (const f of runMedia.data?.media ?? []) if (f.item_key) (by[f.item_key] ??= []).push(f);
    return by;
  }, [runMedia.data]);
  const onNotes = useMemo(() => {
    const got: Record<string, number> = {};
    for (const r of notes.data?.receipts ?? []) {
      if (!r.result.proves_arrival) continue;
      for (const b of r.result.blocks) for (const row of b.rows) {
        const k = gkey(row.order_id, row.name);
        if (row.received > 0) got[k] = Math.max(got[k] ?? 0, row.received);
      }
    }
    return got;
  }, [notes.data]);
  // Reading the list the job is NOT ticked in: the counters and the finish still follow its own list.
  const ownList = q.data && !q.data.editable && q.data.run ? q.data.run.furniture_list === "orders" : null;
  // Read at the pace of the list shown, so another device's ticks reach the head counters too.
  const own = useQuery({ queryKey: ["prep-event", eventId, ownList], queryFn: () => fetchDelivery(eventId, ownList), enabled: ownList != null,
    refetchInterval: saving ? false : 15_000 });
  // His sheet head carries the card's pills: the same figures, read for this job.
  const progress = useQuery({ queryKey: ["job-progress", String(eventId)], queryFn: () => fetchProgress([eventId]), staleTime: 30_000, refetchInterval: 30_000,
    gcTime: KEEP_READS });
  // His `docsHtml` is on every job sheet: shown on the Delivery Manager's list, the supplier orders and
  // their documents are read from his list — on their own pace, not again after every tick.
  const docs = useQuery({ queryKey: ["prep-docs", eventId], queryFn: () => fetchDelivery(eventId, true),
    enabled: q.data != null && q.data.list !== "orders", staleTime: 60_000, refetchInterval: 60_000 });
  // His local state: every action that moves a job between groups (running, paused, to do, finished)
  // moves its card at the tap, not when the server answers — to the tab his `tabOf` gives its new run
  // (`place`), Archived included; refused, the list comes back as it was. A list read already on its way
  // is dropped first, or it would land after the tap with the old group. A change is not a read: each list
  // keeps the moment it was read, which its sync line shows and its card numbers are dated by.
  const setList = (change: (j: AssemblyJob) => AssemblyJob, id: number) => {
    for (const [key, old] of qc.getQueriesData<JobsResponse>({ queryKey: ["prep-jobs"] })) {
      if (old) qc.setQueryData<JobsResponse>(key, { ...old, jobs: old.jobs.map((j) => (j.id === id ? place(change(j), old.today) : j)) },
        { updatedAt: qc.getQueryState(key)?.dataUpdatedAt });
    }
  };
  const patchList = async (change: (j: AssemblyJob) => AssemblyJob) => {
    await qc.cancelQueries({ queryKey: ["prep-jobs"] });
    const before = qc.getQueriesData<JobsResponse>({ queryKey: ["prep-jobs"] });
    setList(change, eventId);
    return before;
  };
  const undoList = (_e: unknown, _v: unknown, before?: Awaited<ReturnType<typeof patchList>>) => {
    before?.forEach(([key, data]) => qc.setQueryData(key, data));
    void qc.invalidateQueries({ queryKey: ["prep-jobs"] });
  };
  // His `enqueue`: a write that did not go through is said (his e.write toast) — here the card also goes
  // back to where it was, since it moved at the tap.
  const failed = (e: unknown, v: unknown, before?: Awaited<ReturnType<typeof patchList>>) => {
    undoList(e, v, before);
    toast(t("job.writeFailed"));
  };
  // A job not started yet gets its run on the card at once (the server makes it with the same tap).
  const cardRun = (j: AssemblyJob, status: string, team: string[]) => j.run
    ? settle(j.run, status === "running" ? team.length : 0, { status, team })
    : settle({ id: 0, status, number: null, finished_at: null, furniture_list: q.data?.list ?? "delivery" }, status === "running" ? team.length : 0, { team });
  // His `startWith`: a paused job starts with the names picked, any other keeps its team and adds them.
  const started = (f: string[]) => patchList((j) => {
    const team = [...new Set([...(j.run?.status === "paused" ? [] : j.run?.team ?? []), ...f])];
    return { ...j, run: cardRun(j, "running", team) };
  });
  // His `pp`, `naCount` and reception pill read the live run, card and head alike. Reopened, they show the
  // work really done at the tap — counted from the job's own list as the server counts it (the list read
  // here, else the reopen's own answer, which is that list) — never the 100 % a read made while finished
  // carries, nor its "📄 received". Finished, the 100 %, nothing not arrived, the "Tick all" counts and his
  // "📄 received" once a delivery note proves the arrival (JobProgress.reception_pill). Held as one more
  // read, at the tap's time, until a newer one made for its status comes; false when the work done cannot
  // be counted yet (no figures held, or the own list not read).
  const tapWork = (status: "paused" | "finished", from: { rows?: Item[]; all?: { assembled?: boolean; checked?: boolean } } = {}) => {
    const base = (summary && progressOf(qc, summary, listAt)) || progress.data?.progress[String(eventId)];
    if (!base) return false;
    let now: Partial<JobProgress>;
    if (status === "finished") {
      const proves = notes.data?.receipts.some((r) => r.result.proves_arrival) ?? false;
      const full = (c: JobProgress["assembled"], on?: boolean) => (on ? { done: c.total, total: c.total } : c);
      now = { work_pct: 100, not_arrived: 0, assembled: full(base.assembled, from.all?.assembled), checked: full(base.checked, from.all?.checked),
        reception: base.reception === null && proves ? "ok" : base.reception };
    } else {
      const rows = from.rows ?? (q.data && (q.data.editable ? q.data : own.data)?.packs.flatMap((p) => p.items));
      if (!rows) return false;
      const notArrived = rows.reduce((s, i) => {
        const d = Math.min(i.qty, i.needs_assembly ? i.assembled_qty : i.checked_qty);
        const onNote = i.qty > 0 && Math.min(onNotes[gkey(i.order_id, i.name)] ?? 0, i.qty) >= i.qty;
        return s + (i.arrived != null && !i.arrived && !onNote && d < i.qty ? i.qty - d : 0);
      }, 0);
      now = { work_pct: workDone(rows), not_arrived: notArrived, reception: base.reception === "ok" ? null : base.reception };
    }
    qc.setQueryData(["job-progress", "tap", eventId], { progress: { [eventId]: { ...base, ...now, status } } });
    return true;
  };
  const dropTap = () => qc.removeQueries({ queryKey: ["job-progress", "tap", eventId], exact: true });
  // A write's answer. The sheet that asked for it closes itself: an answer never closes a sheet opened since.
  const after = (data: Delivery) => {
    // The answer is the run's own list: it replaces the screen when that is the list shown, and only the
    // OTHER list is fetched again — refetching the shown one raced the next tick and put back a stale count.
    const shown = fromOrders == null || (fromOrders ? "orders" : "delivery") === data.list;
    if (shown) qc.setQueryData(["prep-event", eventId, fromOrders], data);
    // His card and head read one live job: the figures the answer carries, worked out on the job's own
    // list for the status it now has, go on both with the run — not one read later, and not read again.
    const mine = String(eventId);
    if (data.progress) qc.setQueryData(["job-progress", mine], { progress: { [mine]: data.progress } });
    // His card and his timer read one run: the answer goes on the card too, before the list is read again.
    if (data.run) setList((j) => ({ ...j, run: fromAnswer(j.run, data.run!) }), eventId);
    void qc.invalidateQueries({ queryKey: ["prep-event", eventId], predicate: (x) => !shown || x.queryKey[2] !== fromOrders });
    void qc.invalidateQueries({ queryKey: ["prep-jobs"] });
    void qc.invalidateQueries({ queryKey: ["job-progress"], predicate: (x) => !data.progress || x.queryKey[1] !== mine });
    // His toast and his `claim`: a fitter started here has left the other job they were on, and that
    // job drops them at once — its sheet (team, clock, status) and its card — before it is read again.
    if (data.released?.length) {
      toast(data.released.map((r) => t("start.moved", { n: r.names.join(", "), c: r.client ?? "—" })).join(" · "));
      const at = new Date().toISOString();
      for (const r of data.released) {
        if (r.event_id == null) continue;
        qc.setQueriesData<Delivery>({ queryKey: ["prep-event", r.event_id] }, (old) => (old?.run ? { ...old, run: leaveRun(old.run, r.names, r.status, at) } : old));
        setList((j) => {
          if (!j.run) return j;
          const team = (j.run.team ?? []).filter((n) => !r.names.includes(n));
          const status = r.status ?? (j.run.status === "running" && !team.length ? "paused" : j.run.status);
          return { ...j, run: settle(j.run, status === "running" ? team.length : 0, { status, team }) };
        }, r.event_id);
      }
      void qc.invalidateQueries({ queryKey: ["fitters"] });
      void qc.invalidateQueries({ queryKey: ["prep-event"], predicate: (x) => x.queryKey[1] !== eventId });
    }
  };
  // His `openThanks(id, sentTo)`: on a finished job, once per job and page session.
  const thank = (data: Delivery, sent: boolean) => {
    if (data.run?.status !== "finished" || THANKED.has(eventId)) return;
    THANKED.add(eventId);
    setThanks({ data, sentTo: sent ? repName(data.addressees ?? null) : "" });
  };
  // His finGo's end: the sheet closes, his toast ("Job finished", or "…, summary sent to <first name>") and
  // his thank-you, naming the rep when this tap's summary went.
  const afterFinish = (data: Delivery) => {
    const sent = data.recap_outcome === "queued";
    setDialog(null);
    setFin({ done: false, error: null });
    toast(sent ? t("fin.doneSent", { n: repFirst(data.addressees ?? null) }) : t("fin.done"));
    thank(data, sent);
  };
  // His closeSheet: a Finish sheet left after its send failed opens the thank-you, without "sent to".
  const closeFin = () => {
    if (fin.done && q.data) thank(q.data, false);
    setDialog(null);
    setFin({ done: false, error: null });
  };
  // His Resume with nobody left on the job (moved elsewhere since this screen was drawn): the picker
  // opens on the job as it is now — the run the refusal carries, else the job read again — with nobody
  // picked, so the fitter now on another job shows as busy there, never as chosen.
  const pickAgain = async (e: unknown) => {
    const run = (e as { body?: { run?: Run | null } }).body?.run;
    if (run) qc.setQueriesData<Delivery>({ queryKey: ["prep-event", eventId] }, (old) => old && { ...old, run });
    else await qc.refetchQueries({ queryKey: ["prep-event", eventId] }).catch(() => {});
    void qc.invalidateQueries({ queryKey: ["fitters"] });
    setDialog("restart");
  };
  // His ticks count at the tap: the screen moves at once, and each tap goes out as +1 / −1 that the
  // server adds to the count as it is then (his `inc` / `dec` on the live run) — a refresh landing
  // between two taps, or a second tablet on the same line, can never take a piece back. "Tick all"
  // sends the count itself.
  const tickScreen = (item: Item, v: QtyChange) => {
    const next = (now: number, set?: number, delta?: number) => set ?? (delta == null ? now : Math.min(item.qty, Math.max(0, now + delta)));
    qc.setQueryData<Delivery>(["prep-event", eventId, fromOrders], (old) => old && {
      ...old,
      packs: old.packs.map((p) => ({ ...p, items: p.items.map((i) => (i.id === item.id && i.kind === item.kind
        ? { ...i, assembled_qty: next(i.assembled_qty, v.assembled, v.assembled_delta), checked_qty: next(i.checked_qty, v.checked, v.checked_delta) } : i)) })),
    });
  };
  const m = {
    start: useMutation({ mutationFn: (f: string[]) => startRun(eventId, f, q.data?.list === "orders"),
      onMutate: started, onError: failed, onSuccess: after }),
    // His "Reprendre": the team is the run's own; with nobody left on it, the picker asks who.
    resume: useMutation({ mutationFn: () => resumeRun(eventId),
      onMutate: () => patchList((j) => (j.run ? { ...j, run: settle(j.run, j.run.team?.length ?? 0, { status: "running" }) } : j)),
      onError: (e, v, before) => { if ((e as { status?: number }).status === 422) { undoList(e, v, before); void pickAgain(e); } else failed(e, v, before); }, onSuccess: after }),
    pause: useMutation({ mutationFn: () => pauseRun(eventId),
      onMutate: () => patchList((j) => (j.run ? { ...j, run: settle(j.run, 0, { status: "paused" }) } : j)), onError: failed, onSuccess: after }),
    // A job with no run gets its idle run with this team (his Team block before the start).
    addFit: useMutation({ mutationFn: (f: string[]) => addFitters(eventId, f, q.data?.list === "orders"),
      onMutate: (f) => patchList((j) => ({ ...j, run: cardRun(j, j.run?.status ?? "idle", [...new Set([...(j.run?.team ?? []), ...f])]) })),
      onError: failed, onSuccess: after }),
    // His "×": the last fitter off a running job pauses it.
    rmFit: useMutation({ mutationFn: (n: string) => removeFitter(eventId, n),
      onMutate: (n) => patchList((j) => {
        if (!j.run) return j;
        const team = (j.run.team ?? []).filter((x) => x !== n);
        return { ...j, run: cardRun(j, j.run.status === "running" && !team.length ? "paused" : j.run.status, team) };
      }),
      onError: failed, onSuccess: after }),
    qty: useMutation({
      mutationKey: ["qty", eventId], scope: { id: `qty-${eventId}` },
      mutationFn: ({ item, ...change }: { item: Item } & QtyChange) => setQuantity(eventId, item.id, { ...change, line_ids: item.line_ids }),
      // A refresh already on its way would land after the tap and put the count it read back on screen.
      onMutate: async (v) => { await qc.cancelQueries({ queryKey: ["prep-event", eventId] }); tickScreen(v.item, v); },
      // The last tap's answer is the truth; an earlier one would put back a count already passed.
      onSuccess: (data) => { if (qc.isMutating({ mutationKey: ["qty", eventId] }) <= 1) after(data); },
      // A tap the server did not take is said, never undone in silence; the job is read again.
      onError: (e) => { toast(failText(t, e, "job.writeFailed")); void qc.invalidateQueries({ queryKey: ["prep-event", eventId] }); },
    }),
    issue: useMutation({
      mutationFn: (v: Parameters<typeof reportIssue>[1]) => reportIssue(eventId, { ...v, from_orders: q.data?.list === "orders" }),
      onSuccess: (data) => { after(data); setDialog(null); },
    }),
    finish: useMutation({
      mutationFn: (v: Parameters<typeof finishRun>[1]) => finishRun(eventId, v),
      // His finGo: the job is finished on the list at the tap (team cleared, time stopped) — under
      // Finished, unless archived by hand: one finished today is not archived on its own (his `arcAuto`).
      onMutate: (v) => {
        tapWork("finished", { all: { assembled: v.tick_all_assembled, checked: v.tick_all_checked } });
        return patchList((j) => (j.run ? {
          ...j, run: settle(j.run, 0, { status: "finished", team: [], finished_at: new Date().toISOString() }),
        } : j));
      },
      // Refused, the Finish sheet stays open with its ticks and note, and the toast shows over it.
      onError: (e, v, before) => { dropTap(); failed(e, v, before); },
      // His finGo: finished, the job's summary queued or not sent by choice — his toast, and his `openThanks`
      // once. A summary that could not go out keeps the sheet open (his catch).
      onSuccess: (data, v) => {
        after(data);
        if (v.send_recap && data.recap_outcome === "failed") return setFin({ done: true, error: t("issue.sendFailed") });
        afterFinish(data);
      },
    }),
    // His finGo on a finished job — the resend sheet, or the Finish sheet whose send failed: the same toast and
    // thank-you once it goes; failed again, the sheet stays open with the error.
    recap: useMutation({ mutationFn: (v: { notes?: string; send: boolean }) => sendRecap(eventId, v.notes, v.send), onError: () => toast(t("job.writeFailed")),
      onSuccess: (data, v) => {
        after(data);
        if (v.send && data.recap_outcome === "failed") return setFin((f) => ({ ...f, error: t("issue.sendFailed") }));
        afterFinish(data);
      } }),
    // His "reopen": paused again — under Today, or Archived when its delivery has passed (his `isArch`).
    // At the tap when the work done can be counted; otherwise with the answer, which is the job's own list
    // and carries its figures — the card never reads "Paused" over a 0 % bar.
    reopen: useMutation({ mutationFn: () => reopenRun(eventId),
      onMutate: () => (tapWork("paused") ? patchList((j) => (j.run ? { ...j, run: { ...j.run, status: "paused", finished_at: null } } : j)) : undefined),
      onError: (e, v, before) => { dropTap(); failed(e, v, before); },
      onSuccess: (data, _v, before) => { if (!before) tapWork("paused", { rows: data.packs.flatMap((p) => p.items) }); after(data); } }),
    reset: useMutation({ mutationFn: () => resetRun(eventId), onError: () => toast(t("job.writeFailed")), onSuccess: (data) => { after(data); toast(t("rst.done")); } }),
    undoReset: useMutation({ mutationFn: () => undoResetRun(eventId), onError: () => toast(t("job.writeFailed")), onSuccess: (data) => { after(data); toast(t("rst.undone")); } }),
  };
  // A tap on the run is answered before the next one is taken: a double click never opens two clocks.
  const busy = m.start.isPending || m.resume.isPending || m.pause.isPending || m.addFit.isPending || m.rmFit.isPending || m.finish.isPending || m.reopen.isPending;

  if (q.isPending) return <p className="text-sm text-slate-400">{t("loading")}</p>;
  if (q.error) return <Problem message={(q.error as Error).message} />;
  const d = q.data!;
  const run = d.run;
  // His photos before the start: make the job's run (idle, in the list shown), then show its files.
  const ensureRun = async () => {
    if (run) return run.id;
    const r = await mediaRun(eventId, d.list === "orders");
    await qc.invalidateQueries({ queryKey: ["prep-event", eventId] });
    return r.run_id;
  };
  // His card's two counts are two sets of pieces, as one counter per line gives them: `toBuild` the
  // pieces to assemble ("assembled"), `toCheck` those only opened and checked ("checked").
  const totals = (d.editable ? d : own.data ?? d).packs.flatMap((p) => p.items).reduce(
    (a, i) => (builds(i)
      ? { ...a, total: a.total + i.qty, toBuild: a.toBuild + i.qty, assembled: a.assembled + Math.min(i.qty, i.assembled_qty) }
      : { ...a, total: a.total + i.qty, toCheck: a.toCheck + i.qty, checked: a.checked + Math.min(i.qty, i.checked_qty) }),
    { total: 0, toBuild: 0, assembled: 0, toCheck: 0, checked: 0 }
  );

  const ev = d.event as { number?: number | null; client_name?: string | null; sales_reps?: { name: string | null; email: string }[] };
  const number = summary?.number ?? ev.number ?? null;
  // His sheet names one sales rep: the contract's owner by name (`a.owner`, the planning's owner names),
  // else "not found" — a rep known only by an email address is not named, as his.
  const owner = summary?.owner ?? (ev.sales_reps ?? []).map((r) => r.name).find(Boolean) ?? null;
  // His `a.est`, the ONE estimate the head, the timer, the gauge, the finished line and the Finish dialog
  // read — the server's, live while the planning entry exists, the run's copy once it is gone. (The rest
  // of the chain only covers a server answer that does not carry it yet.)
  const estimate = d.estimate_minutes ?? run?.estimated_minutes ?? summary?.estimate_minutes ?? d.estimated_minutes ?? 0;
  // His card's client, the name from the entry's title: the card's own job, else the sheet's (`d.job`).
  const client = summary?.client ?? d.job?.client ?? run?.client_name ?? ev.client_name ?? "";
  const status = run?.status === "idle" ? undefined : run?.status;
  // His head is `pills(j)` and "day · warehouse" whatever opened the sheet: the card's own job, else the
  // job as the sheet itself says it (`sheetJob`).
  const head = summary ?? sheetJob(eventId, d, client, number);
  const cardStatus = head.run?.status === "idle" ? undefined : head.run?.status;
  const finished = status === "finished";
  const warehouseId = (d.event.warehouse as { id?: number } | null)?.id ?? summary?.warehouse?.id ?? null;
  // His "Copy #n" puts "#n" on the clipboard, and his toast "#n copied".
  const copy = () => { if (number) void navigator.clipboard?.writeText(`#${number}`).then(() => toast(t("job.copied", { n: number }))); };
  const allItems = d.packs.flatMap((p) => p.items);
  // This sheet's own times, on its warehouse's clock (its children read it from WarehouseZone).
  const stamp = (iso: string) => stampAt(iso, locale, zoneOf(warehouseId));
  // The run's own list, for what an issue names: an issue is reported on the list the job is ticked in.
  const ownItems = (d.editable ? d : own.data ?? d).packs.flatMap((p) => p.items);
  return (
    // One clock for the job: its issues, its summary, its reset and its photo captions are written in its
    // warehouse's time, as its Slack messages are.
    <WarehouseZone.Provider value={zoneOf(warehouseId)}>
    <div>
      {thanks && <ThankYou data={thanks.data} sentTo={thanks.sentTo} client={client} eventId={eventId} onClose={() => setThanks(null)} />}
      {/* His job head: back to the list (a phone only), the event number for the timesheet, the client with
          his pills, then the four facts. */}
      {onClose && <button onClick={onClose} className="mb-2 text-sm text-slate-500 underline min-[980px]:hidden">← {t("job.backToList")}</button>}
      {number && (
        <div className="mb-3 flex items-center gap-3 rounded-lg bg-slate-100 px-4 py-2 text-sm">
          <span className="text-slate-600">{t("job.timesheetNo")}</span>
          <b className="font-mono text-lg">#{number}</b>
          <button onClick={copy} className="ml-auto rounded-lg bg-white px-3 py-1 ring-1 ring-slate-300">{t("job.copyNo")} #{number}</button>
        </div>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="text-xl font-semibold text-slate-900">{client}</h2>
        {/* His head shows the card's own `pills(j)`: the card's status (moved at the tap) and the same
            answer the card picks for it. */}
        <span className="flex flex-wrap gap-1 text-xs"><Pills job={head} status={cardStatus} progress={progressOf(qc, head, summary ? listAt : 0) ?? null} /></span>
      </div>
      {/* His archBox is on every sheet (index.html:799/823): the card's job, else the job as the sheet says it. */}
      {isArch(head) && <ReadOnlyArea module="preparation"><ArchiveBox job={head} onArchived={(msg) => { onToast(msg); }} /></ReadOnlyArea>}
      <dl className="mb-4 grid grid-cols-2 gap-x-6 gap-y-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm sm:grid-cols-4">
        {/* His "day · warehouse", each part empty when unknown (`dayLbl(a.day) · WH[a.wh]||""`). */}
        <Fact label={t("jobs.asm")} value={`${dayLbl(head.day || run?.event_on || null)} · ${head.warehouse?.name ?? ""}`} />
        <Fact label={t("jobs.deliv")} value={head.delivery_on ?? run?.delivery_on ? dayLbl(head.delivery_on ?? run?.delivery_on ?? null) : <i>{t("jobs.noDelivery")}</i>} />
        <Fact label={t("jobs.est")} value={<span className="font-mono">{fmin(estimate)}</span>} />
        <Fact label={t("job.salesRep")} value={owner ?? t("job.noRep")} />
      </dl>
      {/* Measured against his estimate: the one in the entry's title, as on the card; frozen once finished. */}
      <ReadOnlyArea module="preparation">
      <div className="mb-4 rounded-xl border border-slate-200 bg-white px-5 py-4">
        <TimerBlock run={status ? run : null} estimate={estimate} />
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Team run={run} busy={busy} onAdd={() => setDialog("addFitter")} onRemove={(n) => m.rmFit.mutate(n)} />
          {totals.toBuild > 0 && <Stat label={t("job.assembled")} value={`${totals.assembled}/${totals.toBuild}`} />}
          {totals.toCheck > 0 && <Stat label={t("job.checked")} value={`${totals.checked}/${totals.toCheck}`} />}
          {run?.blocked && status !== "finished" && <Badge tone="red">{t("item.blocked")}</Badge>}
          {d.delivery_manager_to_tick > 0 && <Badge tone="amber">{t("job.dmToTick", { n: d.delivery_manager_to_tick })}</Badge>}
          <div className="ml-auto flex flex-wrap gap-2">
            {finished ? (
              <>
                <button onClick={() => m.reopen.mutate()} disabled={busy} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-40">
                  {t("finish.reopen")}
                </button>
                {run!.recap_sent_via !== "slack" && (
                  <button onClick={() => openFin("resend")} disabled={m.recap.isPending}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm disabled:opacity-40">{t("finish.resend")}</button>
                )}
              </>
            ) : (
              <>
                {status === "running" ? (
                  <button onClick={() => m.pause.mutate()} disabled={busy} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-40">
                    {t("run.pause")}
                  </button>
                ) : (
                  // His "Reprendre": a paused job restarts with its team, without asking again; his "Démarrer"
                  // on a job that already has its team starts with it too.
                  <button onClick={() => (run && run.team.length && status === "paused" ? m.resume.mutate() : setDialog("start"))} disabled={busy}
                    className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
                    {status ? t("run.resume") : t("job.start")}
                  </button>
                )}
                {status && (
                  <button onClick={() => openFin("finish")} disabled={busy} className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
                    {t("finish.send")}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      </ReadOnlyArea>

      {/* His step 6, stated as a requirement on the job while there is still work to do (`packHtml`). */}
      {!finished && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-5 py-3">
          <p className="text-sm font-semibold text-amber-900">⚠ {t("job.packing")}</p>
          <p className="mt-0.5 text-xs text-amber-800">{t("job.packingBody")}</p>
        </div>
      )}
      {/* His "Signaler un problème", only for a job without any item to report it on (index.html:813): a
          job with items has its "⚠ Issue" on each line. Not from the list the job is not ticked in. */}
      {!finished && allItems.length === 0 && (
        <ReadOnlyArea module="preparation">
          <button onClick={() => setDialog({ issueFor: null })} disabled={!d.editable}
            className="mb-4 rounded-lg border border-red-300 px-3 py-2 text-sm font-semibold text-red-700 disabled:opacity-40">
            {t("issue.report")}
          </button>
        </ReadOnlyArea>
      )}

      {/* His "Problèmes signalés": newest first, who and when, the piece, the text, how it went out. */}
      {d.issues.length > 0 && (
        <div className="mb-4">
          <h3 className="mb-1.5 text-base font-semibold text-slate-900">{t("issue.reported")}</h3>
          <ul className="space-y-1.5">
            {[...d.issues].sort((a, b) => b.reported_at.localeCompare(a.reported_at)).map((i) => (
              <li key={i.id} className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-800">
                <div className="flex flex-wrap justify-between gap-x-3">
                  <b>{t(`issue.${i.kind}`)}{i.blocks_assembly && <> · {t("item.blocked")}</>}</b>
                  <span className="text-xs text-red-600">{[i.reported_by.join(", "), stamp(i.reported_at)].filter(Boolean).join(" · ")}</span>
                </div>
                {issueItem(t, [ownItems, allItems], i) && <span className="block text-xs text-red-700">{issueItem(t, [ownItems, allItems], i)}</span>}
                <p className="mt-0.5">{i.description}</p>
                <span className="block text-xs text-red-600"><IssueSent issue={i} /></span>
                {/* Its photos open for everyone; a read-only viewer gets no "add" (MediaStrip). */}
                <div className="mt-2"><MediaStrip type="issue" id={i.id} /></div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* His "Photos et vidéos de contrôle": the furniture packed and ready, counted in the récap. His
          buttons are there from the first moment (`addBtns`): before the start, the first photo or file
          makes the job's run (idle, in the list shown), and a tablet asked names the planning entry — a
          tap alone makes nothing. A read-only viewer sees them all, without the buttons that add one. */}
      <div className="mb-4 rounded-xl border border-slate-200 bg-white px-5 py-3">
        <MediaStrip type="run" id={run?.id ?? null} ensure={ensureRun} eventId={eventId} fromOrders={d.list === "orders"} job itemKey={null}
          tabletLabel={`${client} — ${t("st.gen")}`} />
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("job.furniture")}</h2>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={d.list === "orders"} onChange={(e) => setFromOrders(e.target.checked)} />
          {t("job.fromOrders")}
        </label>
      </div>
      {!d.editable && (
        <p className="mb-3 rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-900 ring-1 ring-amber-200">{t("job.otherList")}</p>
      )}
      {d.list === "orders" && <CitedOrders d={d} />}
      {/* Each write on a line is greyed for a read-only viewer on its own (PackCard): its product page and
          its photos still open, as his. */}
      <JobClient.Provider value={{ client, number, eventId, fromOrders: d.list === "orders" }}>
      <EnsureRun.Provider value={ensureRun}>
      <div className="space-y-4">
        {/* His "Meubles à monter" is always there on his list, with his line when the order gave none. */}
        {d.list === "orders" && !d.packs.some((p) => p.section === "assemble") && (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 bg-slate-50 px-5 py-2.5"><h3 className="text-sm font-semibold text-slate-800">{t("job.toAssemble")}</h3></div>
            <p className="px-5 py-3 text-sm text-slate-500">{t("job.noItems")}</p>
          </div>
        )}
        {d.packs.map((p) => (
          <PackCard key={p.id} pack={p} editable={d.editable} finished={finished} onNotes={onNotes} runId={runId} lineMedia={lineMedia}
            onQty={(item, v) => m.qty.mutate({ item, ...v })}
            onIssue={(itemId) => setDialog({ issueFor: itemId })} />
        ))}
        <MaybeBlock items={(d.maybe ?? []).filter((i) => !i.unseen)} admin={d.admin_url} />
        <MaybeBlock items={(d.maybe ?? []).filter((i) => i.unseen)} admin={d.admin_url} since={d.maybe_since ?? null} />
      </div>
      </EnsureRun.Provider>
      </JobClient.Provider>

      {/* His "Réception (documents fournisseur lus)": what the supplier documents read at Réception say
          arrived for this delivery. */}
      <ReceivedForJob eventId={eventId} />
      <SupplierDocuments orders={d.list === "orders" ? d.orders ?? [] : docs.data?.orders ?? null} admin={d.admin_url ?? ""}
        eventId={eventId} fromOrders={d.list === "orders"} runId={runId} client={client} onEnsureRun={ensureRun} />
      {run && <ManagerReset run={run} busy={m.reset.isPending || m.undoReset.isPending} onReset={() => m.reset.mutate()} onUndo={() => m.undoReset.mutate()} />}
      {/* His archive box sits at the bottom while the client is not archived; archiving closes the sheet. */}
      {!isArch(head) && (
        <ReadOnlyArea module="preparation"><ArchiveBox job={head} onArchived={(msg) => { onToast(msg); onClose?.(); }} /></ReadOnlyArea>
      )}
      <JobLinks d={d} />

      {/* His `pickGo`: the picker closes at the tap, and the start (or the names added) goes out after. */}
      {(dialog === "start" || dialog === "restart") && (
        <StartDialog warehouseId={warehouseId} eventId={eventId} heading={startHeading(number, client, head.warehouse?.name)} busy={m.start.isPending}
          initial={dialog === "restart" ? [] : run?.team ?? []} onFailed={toast} onCancel={() => setDialog(null)} onStart={(f) => { setDialog(null); m.start.mutate(f); }} />
      )}
      {dialog === "addFitter" && (
        <StartDialog warehouseId={warehouseId} eventId={eventId} heading={startHeading(number, client, head.warehouse?.name)} exclude={run?.team ?? []} adding busy={m.addFit.isPending}
          onFailed={toast} onCancel={() => setDialog(null)} onStart={(f) => { setDialog(null); m.addFit.mutate(f); }} />
      )}
      {(dialog === "finish" || dialog === "resend") && run && (
        <FinishDialog run={run} totals={totals} issues={d.issues.length} heading={`${number ? `#${number} ` : ""}${client}`}
          estimate={estimate} addressees={d.addressees ?? null} client={client} resendOnly={dialog === "resend"} busy={m.finish.isPending || m.recap.isPending}
          checks={(d.editable ? d : own.data ?? d).packs.flatMap((p) => p.items).filter((i) => i.needs_assembly === false)
            .reduce((a, i) => ({ done: a.done + Math.min(i.qty, i.checked_qty), total: a.total + i.qty }), { done: 0, total: 0 })}
          finished={fin.done} error={fin.error} onCancel={closeFin}
          onFinish={(v) => {
            // His note("") at each send: the error goes while the next one is tried.
            setFin((f) => ({ ...f, error: null }));
            if (dialog === "resend" || fin.done) m.recap.mutate({ notes: v.recap_notes, send: v.send_recap });
            else m.finish.mutate({ ...v, estimated_minutes: estimate });
          }} />
      )}
      {dialog && typeof dialog === "object" && (
        <IssueDialog items={allItems} preselect={dialog.issueFor}
          heading={`${number ? `#${number} ` : ""}${client}`} team={finished ? [] : run?.team ?? []}
          warehouseId={warehouseId} addressees={d.addressees ?? null} sending={m.issue.isPending}
          error={m.issue.error ? failText(t, m.issue.error, "issue.netFailed") : null}
          onFailed={toast} onCancel={() => { m.issue.reset(); setDialog(null); }} onReport={(v) => m.issue.mutate(v)} />
      )}
    </div>
    </WarehouseZone.Provider>
  );
}

// A job no list holds, as its sheet says it: the job the list would carry (`d.job`, AssemblyJobs#one —
// its delivery day read from its title, its archive state), with the sheet's own run and issues, which
// every tap here moves — filed by his `isArch` on those (`place`, on the warehouse's day), so its head
// carries his pills and his archive box as a card would.
function sheetJob(eventId: number, d: Delivery, client: string, number: number | null): AssemblyJob {
  const ev = d.event as { starts_at?: string | null; warehouse?: { id: number; name: string } | null };
  const r = d.run;
  const base: AssemblyJob = d.job ?? {
    id: eventId, number, assembly: false, title: null, client, contract_id: null, contract_name: null,
    day: r?.event_on ?? ev.starts_at?.slice(0, 10) ?? "", starts_at: ev.starts_at ?? null, delivery_on: r?.delivery_on ?? null,
    estimate_text: null, estimate_minutes: null, warehouse: ev.warehouse ?? null, type_label: null,
    approved: null, sales_approved: null, logistics_approved: null,
    run: null, tab: "today", archive: { archived: false, how: null, by: null, at: null },
    issues: 0, gone: false,
  };
  return place({ ...base, number: base.number ?? number, client: base.client || client,
    run: r ? fromAnswer(base.run, r) : null, issues: d.issues.length }, todayInZurich());
}

// His start sheet names the job: "#n client · warehouse".
function startHeading(number: number | null, client: string, warehouse: string | null | undefined) {
  return [`${number ? `#${number} ` : ""}${client}`.trim(), warehouse].filter(Boolean).join(" · ");
}

// His `gkey` for a line: its supplier order and its title.
function gkey(orderId: number | null | undefined, title: string | null | undefined) {
  return `${orderId ?? ""}|${title ?? ""}`;
}

// One of his four facts under the client's name.
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="font-medium text-slate-900">{value}</dd>
    </div>
  );
}

// How an issue's message went out: sent (to whom, when), waiting to be sent, or failed.
function IssueSent({ issue }: { issue: Issue }) {
  const { t } = useT();
  const { stamp } = useFormat();
  const d = issue.delivery;
  const sent = issue.notified_at ? t("issue.sentTo", { n: issue.notified_to.join(", "), t: stamp(issue.notified_at) }) : null;
  const failed = d?.failed_to.length ? t("issue.notSent", { n: d.failed_to.join(", ") }) : d?.state === "failed" ? t("issue.sendFailed") : null;
  if (sent || failed) return <>{[sent, failed].filter(Boolean).join(" · ")}</>;
  return d ? <>{t("issue.sending")}</> : null;
}

// His `teamHtml`, on every job: each fitter with their own time (live while it runs) and ×; those who
// left, greyed with their time; "Nobody yet" before anyone; "+ Fitter" while the job is not finished.
function Team({ run, busy, onAdd, onRemove }: {
  run: Delivery["run"]; busy: boolean; onAdd: () => void; onRemove: (name: string) => void;
}) {
  const { t, locale } = useT();
  const running = run?.status === "running";
  // The cards' and the timer's own tick (his one 1 s loop rewrites the [data-pt] chips too).
  const now = useNow(running);
  const team = run?.team ?? [];
  // His `perPerson(r, now)`: each one's exact time from the segments, rounded once — the same count the
  // timer line makes, so a chip never reads a minute off it.
  const byPerson = run ? perPersonMinutes(run, now) : {};
  const live = (n: string) => byPerson[n] ?? 0;
  const left = Object.keys(byPerson).filter((n) => !team.includes(n));
  const finished = run?.status === "finished";
  return (
    <div className="flex w-full flex-wrap items-center gap-1.5 text-xs">
      {/* French puts a space before the colon; the other three do not. */}
      <span className="text-slate-500">{t("team.title")}{locale === "fr" ? " :" : ":"}</span>
      {team.length === 0 && <span className="text-slate-400">{t("team.nobody")}</span>}
      {team.map((n) => (
        <span key={n} className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2.5 pr-1 text-slate-800">
          {n} <b className="tabular-nums">{fmin(live(n))}</b>
          <button onClick={() => onRemove(n)} disabled={finished || busy} aria-label={`${t("team.remove")} ${n}`}
            className="h-5 w-5 rounded-full text-slate-500 hover:bg-slate-300 disabled:opacity-30">×</button>
        </span>
      ))}
      {left.map((n) => (
        <span key={n} title={t("team.left")} className="rounded-full bg-slate-50 px-2.5 py-0.5 text-slate-400 line-through decoration-slate-300">
          {n} <b className="tabular-nums">{fmin(byPerson[n])}</b>
        </span>
      ))}
      {!finished && (
        <button onClick={onAdd} disabled={busy} className="rounded-full border border-dashed border-slate-400 px-2.5 py-0.5 text-slate-700 hover:bg-slate-50 disabled:opacity-40">
          {t("team.add")}
        </button>
      )}
    </div>
  );
}

// His `perPerson(r, now)` in minutes: every segment's length (to now while open) for each name on it.
function perPersonMinutes(run: NonNullable<Delivery["run"]>, now: number) {
  const ms: Record<string, number> = {};
  for (const g of run.segments ?? []) {
    if (!g.started_at) continue;
    const dt = Math.max(0, (g.ended_at ? Date.parse(g.ended_at) : now) - Date.parse(g.started_at));
    for (const n of g.worker_names) ms[n] = (ms[n] ?? 0) + dt;
  }
  // A name the segments do not hold (none should) keeps the server's exact count.
  for (const [n, sec] of Object.entries(run.seconds_by_person ?? {})) if (ms[n] == null) ms[n] = sec * 1000;
  return Object.fromEntries(Object.entries(ms).map(([n, v]) => [n, v / 60000]));
}

// The piece an issue is about, as his report (`repForm`) names it: "2 of 4 × Bistro Chair (chairs) –
// order #13735", the type singular or plural by how many are affected (his lbl(o.sl, q)). The piece is
// looked up in the run's own list first — the one issues are reported on — then in the list shown.
function issueItem(t: (k: string, v?: Record<string, string | number>) => string, lists: Item[][], i: Issue) {
  const find = (items: Item[]) => (i.myotaku_order_line_id ? items.find((x) => x.line_ids?.includes(i.myotaku_order_line_id as number))
    : i.myotaku_contract_item_id ? items.find((x) => x.id === i.myotaku_contract_item_id && !x.kind) : undefined);
  const it = lists.map(find).find(Boolean);
  if (!it) return null;
  const affected = it.qty > 1 ? Math.min(it.qty, Math.max(1, i.affected_qty ?? 1)) : 1;
  const q = it.qty > 1 ? t("issue.qOfN", { q: affected, n: it.qty }) : "1";
  const type = it.product_type_slug ? ` (${furnitureType(t, it.product_type_slug, affected)})` : "";
  const name = it.name || (it.product_type_slug ? furnitureType(t, it.product_type_slug, 1) : "");
  return `${q} × ${name}${type}${it.order_id ? ` – ${t("job.order")} #${it.order_id}` : ""}`;
}

// His `fmin`: 65 → "1 h 05", 60 → "1 h", 45 → "45 min".
// His timer block: the elapsed clock to the second (green running, amber paused), the gauge of
// person-time against the estimate (amber from 85 %, red over), and the line under it — computed from
// the run's own time segments, so it ticks without asking the server — on the cards' own 1 s tick, so the
// card beside it reads the same minute at every instant (his #tl1 and [data-el] share one callback).
function TimerBlock({ run, estimate }: { run: Delivery["run"]; estimate: number | null }) {
  const { t } = useT();
  const running = run?.status === "running";
  const now = useNow(running);
  let wallMs = 0, personMs = 0;
  for (const s of run?.segments ?? []) {
    if (!s.started_at) continue;
    const ms = Math.max(0, (s.ended_at ? Date.parse(s.ended_at) : now) - Date.parse(s.started_at));
    wallMs += ms;
    // His `times`: a segment counts the people named on it — none, none.
    personMs += ms * s.worker_names.length;
  }
  const pm = personMs / 60000, est = estimate ?? 0;
  const tone = est && pm > est ? "bg-red-500" : est && pm > est * 0.85 ? "bg-amber-500" : "bg-emerald-500";
  const team = run?.team ?? [];
  return (
    <div>
      <div className={`font-mono text-5xl font-bold tabular-nums leading-none ${running ? "text-emerald-600" : run?.status === "paused" ? "text-amber-600" : "text-slate-900"}`}>
        {clock(wallMs)}
      </div>
      <div className="relative mt-3 h-3 overflow-hidden rounded-md border border-slate-200 bg-slate-50" role="img" aria-label={t("job.estimate")}>
        <i className={`absolute inset-y-0 left-0 rounded ${tone}`} style={{ width: `${est ? Math.min(100, Math.round((pm / est) * 100)) : 0}%` }} />
      </div>
      <div className="mt-2 flex flex-wrap justify-between gap-x-3 gap-y-1 text-sm text-slate-600">
        {!run ? (
          <>
            <span>{t("timer.notStarted")}</span>
            <span>{t("jobs.est")} <b>{fmin(est)}</b></span>
          </>
        ) : run.status === "finished" ? (
          <>
            <span>{bold(t("timer.doneSum"), { r: fmin(pm), e: fmin(est) })}</span>
            <RecapState run={run} />
          </>
        ) : (
          <>
            <span>{team.length > 1 ? bold(t("timer.teamTime"), { n: String(team.length), t: fmin(pm) }) : bold(t("timer.ofEst"), { t: fmin(pm), e: fmin(est) }, ["t"])}</span>
            {est > 0 && <span>{pm > est ? t("timer.over", { t: fmin(pm - est) }) : t("timer.left", { t: fmin(est - pm) })}</span>}
          </>
        )}
      </div>
    </div>
  );
}

// His recap line once finished: sent (to whom, when), not sent, or — when Slack did not take it — failed
// or not confirmed yet, with the time it was tried.
function RecapState({ run }: { run: NonNullable<Delivery["run"]> }) {
  const { t } = useT();
  const { stamp } = useFormat();
  if (!run.recap_sent_at) return null;
  const when = stamp(run.recap_sent_at);
  if (run.recap_sent_via === "failed") return <span className="font-medium text-red-700">{t("recap.failed")} · {when}</span>;
  if (run.recap_sent_via === "unclear") return <span className="text-amber-700">{t("recap.unclear")} · {when}</span>;
  if (run.recap_sent_via === "none") return <span>{t("recap.none")} · {when}</span>;
  return <span>{t("finish.recapSent")} {(run.recap_sent_to ?? []).join(", ")} · {when}</span>;
}

// His `fclock`: hh:mm:ss.
function clock(ms: number) {
  const s = Math.floor(Math.max(0, ms) / 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor(s / 60) % 60)}:${pad(s % 60)}`;
}

// A translated sentence with its values in bold, as his; `only` limits which ones.
function bold(template: string, vars: Record<string, string>, only?: string[]) {
  return template.split(/(\{\w+\})/).map((part, i) => {
    const k = /^\{(\w+)\}$/.exec(part)?.[1];
    if (!k || vars[k] == null) return <Fragment key={i}>{part}</Fragment>;
    return !only || only.includes(k) ? <b key={i}>{vars[k]}</b> : <Fragment key={i}>{vars[k]}</Fragment>;
  });
}


function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-sm">
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="font-semibold text-slate-900">{value}</div>
    </div>
  );
}

// The supplier orders his list is read from: supplier, arrival, and any cited order the feed would not show.
function CitedOrders({ d }: { d: Delivery }) {
  const { t } = useT();
  const unreadable = d.unreadable_order_ids ?? [];
  if (!d.cited_order_ids?.length) return <p className="mb-3 text-sm text-slate-500">{t("job.noOrders")}</p>;
  return (
    <div className="mb-3 flex flex-wrap gap-1.5 text-xs">
      {(d.orders ?? []).map((o) => (
        <span key={o.id} className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
          {t("job.order")} #{o.id}{o.supplier ? ` · ${o.supplier}` : ""} · <Arrival on={o.arrival_on} arrived={o.arrived} />
        </span>
      ))}
      {unreadable.length > 0 && (
        <span className="rounded-full bg-red-50 px-2.5 py-1 text-red-800">{t("job.unreadable").replace("{ids}", unreadable.map((i) => `#${i}`).join(", "))}</span>
      )}
    </div>
  );
}

// His arrival wording: arrived on a date, expected on a date, or no date yet.
function Arrival({ on, arrived }: { on: string | null | undefined; arrived: boolean | undefined }) {
  const { t } = useT();
  if (!on) return <>{t("job.noArrival")}</>;
  const d = `${on.slice(8, 10)}.${on.slice(5, 7)}`;
  return <>{arrived ? `✅ ${t("job.arrived").replace("{d}", d)}` : `⏳ ${t("job.expected").replace("{d}", d)}`}</>;
}

// His "Documents fournisseur" (`docsHtml`): per cited order, its arrival, then its documents — the
// client's first, the others folded — each typed from its title, with "not a receipt" unless it is a
// delivery note, and his warning when only a confirmation or an invoice is there.
const DOC_DN = /deliver|livraison|lieferschein|packing|pakkeseddel|f(?:ø|o)lgeseddel|bon de livr|\bBL[\s_-]?\d/i;
const DOC_INV = /invoice|facture|rechnung|faktura|fattura/i;
const DOC_OC = /confirm|best(?:ä|ae?)tig|bekr(?:æ|ae)ft|\bAB[\s_-]?\d|auftrag|\border|commande|bestilling|ordre|pro-?forma|angebot|offre|devis|quot/i;
function docKind(d: SupplierDocument) {
  const title = d.title ?? "";
  return d.is_image ? "img" : DOC_DN.test(title) ? "dn" : DOC_INV.test(title) ? "inv"
    : DOC_OC.test(title) || /^\s*\d{4,}[\s_.-]/.test(title) ? "oc" : "oth";
}
const DOC_KIND = { dn: "docs.kindDn", oc: "docs.kindOc", inv: "docs.kindInv", oth: "docs.kindOth" } as const;
const ddmm = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : "");

function SupplierDocuments({ orders: read, admin, eventId, fromOrders, runId, client, onEnsureRun }: {
  // null while his list's orders are still being read (the sheet shows the other list).
  orders: CitedOrder[] | null; admin: string; eventId: number; fromOrders: boolean; runId: number | null; client: string; onEnsureRun: () => Promise<number>;
}) {
  const orders = read ?? [];
  const { t } = useT();
  const readOnly = useReadOnly("preparation");
  // The photos under "docs", the same list MediaStrip shows: the heading waits for the first one.
  const docsMedia = useQuery({ queryKey: ["media", "run", runId, "docs"], queryFn: () => fetchMedia("run", runId!, "docs"), enabled: runId != null });
  const row = (d: SupplierDocument) => {
    const kind = docKind(d);
    return (
      <li key={d.id} className="flex flex-wrap items-baseline gap-x-2 py-0.5 text-sm">
        <span aria-hidden="true">{d.is_image ? "🖼️" : "📄"}</span>
        <span className="min-w-0 flex-1 text-slate-800">{d.title || (d.is_image ? t("docs.img") : t("docs.pdf"))}
          {kind !== "img" && (
            <span className={`ml-2 rounded px-1.5 text-xs ${kind === "dn" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              {t(DOC_KIND[kind])}{kind === "dn" ? "" : ` · ${t("docs.notRx")}`}
            </span>
          )}
        </span>
        <span className="font-mono text-xs text-slate-500">{ddmm(d.uploaded_at)}</span>
        {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-700 underline">{t("docs.open")}</a>}
      </li>
    );
  };
  const onSite = runId != null && (docsMedia.data?.media ?? []).length > 0;
  // His `D.exp` note, where his sheet hides the links (a sync stopped for 6 days): ours hides them only
  // for documents named from the plan, the feed no longer showing the order; the reason is not his.
  const expired = orders.some((o) => (o.documents ?? []).some((d) => d.kept));
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white px-5 py-4">
      <h3 className="text-base font-semibold text-slate-900">{t("sdocs.title")}</h3>
      <p className="mb-3 text-xs text-slate-500">{t("sdocs.hint")}</p>
      {expired && <p className="mb-3 rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-900 ring-1 ring-amber-200">{t("docs.exp")}</p>}
      {read?.length === 0 && <p className="text-sm text-slate-500">{t("docs.noOrder")}</p>}
      <div className="space-y-3">
        {orders.map((o) => {
          const docs = o.documents ?? [];
          const mine = docs.filter((d) => d.mine), others = docs.filter((d) => !d.mine);
          const onlyPaper = !o.arrived && docs.some((d) => ["oc", "inv", "oth"].includes(docKind(d)));
          return (
            <div key={o.id} className={`rounded-lg border px-3 py-2 ${o.arrived ? "border-slate-200" : "border-red-200 bg-red-50/40"}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <b>{o.supplier ?? ""}</b>
                <a href={`${admin}/catalogue-orders/orders/${o.id}`} target="_blank" rel="noopener noreferrer" className="text-sky-700 underline">#{o.id}</a>
                {o.arrived
                  ? <span className="text-emerald-700">{t("docs.arrOk", { d: ddmm(o.arrival_on) })}</span>
                  : <span className="rounded bg-red-600 px-1.5 text-xs font-semibold text-white">{t("docs.arrNo")}{o.arrival_on ? ` · ${t("job.naExpected", { d: ddmm(o.arrival_on) })}` : ""}</span>}
              </div>
              {onlyPaper && <p className="mt-1 text-sm font-semibold text-red-700">{t("docs.ocWarn")}</p>}
              {o.documents_failed ? <p className="mt-1 text-sm text-amber-800">{t("docs.unread")}</p> : docs.length === 0 ? <p className="mt-1 text-sm text-slate-500">{t("docs.none")}</p> : (
                <>
                  {mine.length > 0 && <ul className="mt-1">{mine.map(row)}</ul>}
                  {others.length > 0 && (mine.length > 0
                    ? <details className="mt-1"><summary className="cursor-pointer text-xs text-slate-500">{t("docs.others", { n: others.length })}</summary><ul>{others.map(row)}</ul></details>
                    : <ul className="mt-1">{others.map(row)}</ul>)}
                </>
              )}
            </div>
          );
        })}
      </div>
      {/* His `docsHtml` foot, in every state: "Photos taken on site" (key "docs") and its photos once there
          is one, and always his single "📷 Photo of the delivery note or boxes" (his `mphoto`: the live
          camera on a desk computer, the camera elsewhere). Before the start, the run is made only when a
          photo is taken or chosen — a tablet asked names the planning entry, and its first photo makes
          the run. A read-only viewer sees the photos, without the button. */}
      {(onSite || !readOnly) && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          {onSite && <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">{t("sdocs.photosT")}</h4>}
          <MediaStrip type="run" id={runId} ensure={onEnsureRun} eventId={eventId} fromOrders={fromOrders} job itemKey="docs" single={t("sdocs.photo")} tabletLabel={`${client} — ${t("st.docs")}`} />
        </div>
      )}
    </div>
  );
}

// His manager box (`rstHtml`): a manager only, when something is recorded or the last reset can still
// be undone. The confirmation lapses after 8 seconds, as his.
function ManagerReset({ run, busy, onReset, onUndo }: { run: NonNullable<Delivery["run"]>; busy: boolean; onReset: () => void; onUndo: () => void }) {
  const { t } = useT();
  const { stamp } = useFormat();
  const { staff } = useAuth();
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (!confirm) return;
    const id = setTimeout(() => setConfirm(false), 8000);
    return () => clearTimeout(id);
  }, [confirm]);
  if (!staff?.is_admin || (!run.recorded && !run.undoable)) return null;
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white px-5 py-4">
      <h3 className="text-base font-semibold text-slate-900">{t("rst.title")}</h3>
      <p className="mt-0.5 text-xs text-slate-500">{t("rst.p")}</p>
      {run.reset_at && <p className="mt-1 text-xs text-slate-500">{t("rst.last", { d: stamp(run.reset_at) })}</p>}
      {confirm && run.status === "running" && <p className="mt-2 text-sm font-medium text-red-700">{t("rst.run")}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {run.recorded && (confirm ? (
          <>
            <button onClick={() => { setConfirm(false); onReset(); }} disabled={busy} className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">{t("rst.ok")}</button>
            <button onClick={() => setConfirm(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm">{t("rst.no")}</button>
          </>
        ) : (
          <button onClick={() => setConfirm(true)} disabled={busy} className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-700 disabled:opacity-40">{t("rst.b")}</button>
        ))}
        {run.undoable && <button onClick={onUndo} disabled={busy} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-40">{t("rst.undo")}</button>}
      </div>
    </div>
  );
}

// His "Liens" (`linksFor`): each supplier order with its supplier and arrival, the assembly event in the
// planning, and the contract — on Myotaku's admin site.
function JobLinks({ d }: { d: Delivery }) {
  const { t } = useT();
  const admin = d.admin_url;
  const ev = d.event as { id?: number; contract_id?: number | null; contract_name?: string | null };
  if (!admin) return null;
  const link = (href: string, text: string) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-sky-700 underline">{text}</a>;
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm">
      <h3 className="mb-2 text-base font-semibold text-slate-900">{t("links.title")}</h3>
      <ul className="space-y-1">
        {(d.orders ?? []).map((o) => (
          <li key={o.id}>
            {link(`${admin}/catalogue-orders/orders/${o.id}`, t("links.order", { id: o.id }))}
            {o.supplier && <span className="text-slate-500"> · {o.supplier}{o.arrival_on ? ` · ${t(o.arrived ? "job.arrived" : "job.expected", { d: ddmm(o.arrival_on) })}` : ""}</span>}
          </li>
        ))}
        {ev.id != null && <li>{link(`${admin}/calendar/events/${ev.id}/edit`, t("links.event"))}</li>}
        {ev.contract_id != null && <li>{link(`${admin}/contracts/${ev.contract_id}/newedit/admininformation`, `${t("links.contract", { id: ev.contract_id })}${ev.contract_name ? ` – ${ev.contract_name}` : ""}`)}</li>}
      </ul>
    </div>
  );
}

// His `arrHtml` for one line: not arrived (blinking) while the planning has no arrival, no note shows
// it and it is not done; ✅ arrived on the planning's date; ✅ received on a scanned note; else expected.
function RowArrival({ item, scanned }: { item: Item; scanned: number }) {
  const { t } = useT();
  const done = Math.min(item.qty, item.needs_assembly === false ? item.checked_qty : item.assembled_qty);
  const d = item.arrival_on ? `${item.arrival_on.slice(8, 10)}.${item.arrival_on.slice(5, 7)}` : null;
  const onNote = item.qty > 0 && scanned >= item.qty;
  // A job whose planning entry is gone shows each line's arrival as last read; one never read is not
  // known, and an unknown arrival says nothing rather than "not arrived".
  if (item.arrived == null) return onNote ? <span className="text-emerald-700">{t("job.rxScanned")}</span> : null;
  if (!item.arrived && !onNote && done < item.qty) {
    return <span className="rounded bg-red-600 px-1.5 font-semibold text-white motion-safe:animate-pulse">⏳ {t("job.notArrived")} · {d ? t("job.naExpected", { d }) : t("job.noArrival")}</span>;
  }
  if (item.arrived && d) return <span className="text-emerald-700">✅ {t("job.arrived", { d })}</span>;
  if (onNote) return <span className="text-emerald-700">{t("job.rxScanned")}</span>;
  return <span>{d ? t("job.expected", { d }) : t("job.noArrival")}</span>;
}

// His `lbl`: the furniture type, singular or plural, as the catalogue names it; an unknown type as is.
function furnitureType(t: (key: string) => string, slug: string, n: number) {
  const key = `furn.${n > 1 ? "many" : "one"}.${slug}`;
  const label = t(key);
  return label === key ? slug : label;
}

function PackCard({ pack, editable, finished, onNotes, runId, lineMedia, onQty, onIssue }: {
  pack: Pack;
  runId: number | null;
  lineMedia: Record<string, MediaFile[]>;
  onNotes: Record<string, number>;
  editable: boolean;
  finished: boolean;
  onQty: (item: Item, v: QtyChange) => void;
  onIssue: (itemId: number) => void;
}) {
  const { t } = useT();
  const title = pack.section ? t(pack.section === "assemble" ? "job.toAssemble" : "job.toCheck") : (pack.room_name || pack.name);
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-slate-100 bg-slate-50 px-5 py-2.5">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {pack.standard_kit?.matches && (
          <Badge tone="slate">{t("pack.standardKit")}{pack.standard_kit.qty ? ` ×${pack.standard_kit.qty}` : ""}</Badge>
        )}
        <span className="ml-auto text-xs text-slate-500">
          {[sectionCount(pack.items.filter(builds), "assembled_qty", t("job.assembled")),
            sectionCount(pack.items.filter((i) => !builds(i)), "checked_qty", t("job.checked"))].filter(Boolean).join(" · ")}
        </span>
      </div>
      {pack.section === "check" && <p className="border-b border-slate-100 px-5 py-2 text-xs text-slate-500">{t("job.toCheckHint")}</p>}
      <ul>{pack.items.map((i) => <ItemRow key={i.id} item={i} editable={editable} finished={finished} scanned={i.kind === "order_line" ? onNotes[gkey(i.order_id, i.name)] ?? 0 : 0}
        runId={runId} media={lineMedia[lineKey(i)] ?? []} onQty={onQty} onIssue={onIssue} />)}</ul>
    </div>
  );
}

function ItemRow({ item, editable, finished, scanned, runId, media, onQty, onIssue }: {
  item: Item;
  // His per-item 📷: the job's run (photos attach to it) and this line's files.
  runId: number | null;
  media: MediaFile[];
  // How many of it a scanned delivery note shows received.
  scanned: number;
  editable: boolean;
  finished: boolean;
  onQty: (item: Item, v: QtyChange) => void;
  onIssue: (itemId: number) => void;
}) {
  const { t } = useT();
  const job = useContext(JobClient);
  const ensureRun = useContext(EnsureRun);
  // His read-only viewer: the counter and "⚠ Issue" greyed; the product page and the photos still open.
  const readOnly = useReadOnly("preparation");
  // His `o.t || lbl(o.sl, 1)`: a line without a name reads as its furniture type.
  const name = item.name || (item.product_type_slug ? furnitureType(t, item.product_type_slug, 1) : "");
  const done = builds(item) ? item.assembled_qty : item.checked_qty;
  const complete = done >= item.qty;
  const hasIssue = item.issues.length > 0;
  const [photos, setPhotos] = useState(false);

  return (
    <li className={`border-b border-slate-100 px-5 py-3 last:border-b-0 ${complete ? "bg-emerald-50" : ""}`}>
      <div className="flex items-start gap-3">
        <div className={`min-w-0 flex-1 ${item.parent_item_id ? "pl-4" : ""}`}>
          {/* His row: "n× title", then the type, the arrival, the order, the time, the product sheet. */}
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm text-slate-800"><b className="font-semibold">{item.qty}×</b> {name}</span>
            {hasIssue && <Badge tone="red">{t(item.issues.length > 1 ? "item.probMany" : "item.probOne", { n: item.issues.length })}</Badge>}
          </div>
          {item.kind === "order_line" ? (
            <p className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-slate-500">
              {item.product_type_slug && <span>{furnitureType(t, item.product_type_slug, item.qty)}</span>}
              <RowArrival item={item} scanned={scanned} />
              <span>{t("job.order")} #{item.order_id}</span>
              {builds(item) && lineMinutes(item) != null && <span>~{fmin(lineMinutes(item)!)}</span>}
              {item.product_url && (
                <a href={item.product_url} target="_blank" rel="noopener noreferrer" className="text-sky-700 underline">{t("job.productPage")} ↗</a>
              )}
            </p>
          ) : builds(item) && lineMinutes(item) != null ? <p className="mt-0.5 text-xs text-slate-500">~{fmin(lineMinutes(item)!)}</p> : null}
          {/* His `cartHtml`: how many boxes, then their references from the carton library — or, for a
              supplier whose boxes carry references, that they are not known yet. */}
          {item.kind === "order_line" && (item.cartons ?? 1) > 1 && (
            <div className="mt-1 text-xs text-slate-600">
              <span className="font-medium">{item.qty > 1
                ? t("job.cartonsPer", { n: item.cartons ?? 1, t: (item.cartons ?? 1) * item.qty })
                : t("job.cartons", { n: item.cartons ?? 1 })}</span>
              {(item.box_refs ?? []).length > 0 ? (
                <ul className="mt-0.5 space-y-0.5">
                  {item.box_refs!.map((b) => <li key={b.no}><span className="font-mono">{b.no}</span> {b.title ?? ""}</li>)}
                </ul>
              ) : /^[A-Z]{1,4}-H\d/.test(item.sku ?? "") ? <span className="ml-2 italic text-slate-500">{t("job.boxesUnknown")}</span> : null}
            </div>
          )}
          {item.delivery_comment && <p className="mt-0.5 text-xs text-slate-500">{item.delivery_comment}</p>}
          {item.delivery_team_comment && <p className="mt-0.5 text-xs text-amber-700">{item.delivery_team_comment}</p>}
        </div>
        {item.image_url && <img src={item.image_url} alt="" className="h-12 w-12 shrink-0 rounded object-cover" loading="lazy" />}
      </div>
      {/* ⛔ A line is a QUANTITY: two of the same chest are two pieces of work finished at
          different moments. The ✓ completes the whole line at once, as the video shows.
          ONE counter per line, as his page and video have it ("Monté ET vérifié = je coche. Pas
          avant !"): a piece to build counts assembled AND checked together; a piece only to check
          counts checked. */}
      <div className="mt-2 flex flex-wrap items-center gap-4">
        <Stepper label={t(builds(item) ? "job.assembled" : "job.checked")} value={Math.min(item.qty, done)} max={item.qty} disabled={!editable || readOnly}
          onSet={(v) => onQty(item, builds(item) ? { assembled: v, checked: v } : { checked: v })}
          onStep={(n) => onQty(item, builds(item) ? { assembled_delta: n, checked_delta: n } : { checked_delta: n })} />
        {/* His 📷 opens the line's sheet for everyone; before the start its first photo makes the run,
            which a read-only viewer may not — the sheet opening makes nothing. */}
        {(runId != null || (!finished && ensureRun && !readOnly)) && (
          <button onClick={() => setPhotos(true)} aria-label={t("mediaJob.itemTitle")}
            className="ml-auto rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
            {media.length ? `📷 ${media.length}` : t("mediaJob.itemAdd")}
          </button>
        )}
        {!finished && (
          <button onClick={() => onIssue(item.id)} disabled={!editable || readOnly}
            className="rounded border border-amber-300 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-40">
            ⚠ {t("item.issue")}
          </button>
        )}
      </div>
      {/* Up to 8 of the line's pictures under it, as his strip; a tap opens the line's files. */}
      {media.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {media.slice(0, 8).map((m) => (
            <button key={m.id} onClick={() => setPhotos(true)} className="h-12 w-12 overflow-hidden rounded bg-slate-200 ring-1 ring-slate-300">
              {m.kind === "image" && (m.thumb_url || m.url)
                ? <img src={m.thumb_url ?? m.url ?? ""} alt={t("mediaJob.itemTitle")} className="h-full w-full object-cover" />
                : <span className="text-xs text-slate-600">▶</span>}
            </button>
          ))}
        </div>
      )}
      {photos && (runId != null || ensureRun) && (
        <Dialog title={t("mediaJob.itemTitle")} onClose={() => setPhotos(false)}>
          {/* His sheet (`openMedia`) names the line, then "#number client". */}
          <p className="-mt-1 font-semibold text-slate-800">{item.qty} × {name}{item.product_type_slug || item.order_id
            ? ` (${[item.product_type_slug ? furnitureType(t, item.product_type_slug, item.qty) : "", item.order_id ? `${t("job.order")} #${item.order_id}` : ""].filter(Boolean).join(" – ")})` : ""}</p>
          {(job.number || job.client) && (
            <p className="mb-3 text-sm text-slate-600">{job.number ? <span className="font-mono text-slate-400">#{job.number} </span> : null}{job.client}</p>
          )}
          <MediaStrip type="run" id={runId} ensure={ensureRun ?? undefined} eventId={job.eventId} fromOrders={job.fromOrders} job itemKey={lineKey(item)}
            tabletLabel={`${job.client} — ${item.qty} × ${name}`} />
          <div className="mt-3 flex justify-end">
            <button onClick={() => setPhotos(false)} className="rounded-lg px-4 py-2 text-sm text-slate-600">{t("mediaJob.close")}</button>
          </div>
        </Dialog>
      )}
    </li>
  );
}

// His `~fmin(o.est * o.n)`: the line's planned time, from the minutes per piece.
function lineMinutes(item: Item) {
  return item.estimated_minutes_per_piece != null ? item.estimated_minutes_per_piece * item.qty : item.estimated_minutes;
}

// A piece to build (assembled and checked by one counter) or only to check — his `asm` per line.
function builds(item: Item) {
  return item.needs_assembly !== false;
}

// A section's "n/m assembled" or "n/m checked", only for the pieces of that kind it holds.
function sectionCount(items: Item[], field: "assembled_qty" | "checked_qty", label: string) {
  const total = items.reduce((n, i) => n + i.qty, 0);
  if (!total) return null;
  return `${items.reduce((n, i) => n + Math.min(i.qty, i[field]), 0)}/${total} ${label}`;
}

// His `gkey` for a line: a row of his list by its first order line, a Delivery Manager item by its id.
function lineKey(item: Item) {
  return item.kind === "order_line" ? `line:${item.id}` : `item:${item.id}`;
}

// His line counter: ✓ completes or clears the whole line (a count), − and + are one piece each (his
// `inc` / `dec`, which do nothing when there is nothing left to add or take).
function Stepper({ label, value, max, disabled = false, onSet, onStep }: {
  label: string; value: number; max: number; disabled?: boolean; onSet: (v: number) => void; onStep: (delta: number) => void;
}) {
  const full = value >= max;
  return (
    <fieldset disabled={disabled} className="flex items-center gap-1.5 disabled:opacity-40">
      <span className="w-20 text-xs uppercase tracking-wide text-slate-400">{label}</span>
      <button onClick={() => onSet(full ? 0 : max)} aria-label={label}
        className={`h-7 w-7 rounded-md border text-sm font-bold ${
          full ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-300 bg-white hover:border-slate-500"}`}>
        {full ? "✓" : ""}
      </button>
      <button onClick={() => { if (value > 0) onStep(-1); }} aria-label="−1"
        className="h-7 w-7 rounded border border-slate-300 bg-white text-slate-600">−</button>
      <span className="w-10 text-center text-sm tabular-nums text-slate-700">{value}/{max}</span>
      <button onClick={() => { if (value < max) onStep(1); }} aria-label="+1"
        className="h-7 w-7 rounded border border-slate-300 bg-white text-slate-600">+</button>
    </fieldset>
  );
}

// His dialogs close with Cancel, Escape or a tap outside — Escape the topmost one only, so a dialog
// opened from another (a line's photos from the job) never closes both.
const OPEN_DIALOGS: number[] = [];
let dialogSeq = 0;
function Dialog({ title, children, onClose }: { title: string; children: React.ReactNode; onClose?: () => void }) {
  const [me] = useState(() => ++dialogSeq);
  useEffect(() => {
    OPEN_DIALOGS.push(me);
    return () => { OPEN_DIALOGS.splice(OPEN_DIALOGS.indexOf(me), 1); };
  }, [me]);
  useEffect(() => {
    if (!onClose) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape" && OPEN_DIALOGS[OPEN_DIALOGS.length - 1] === me) onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose, me]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4" role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="max-h-[90vh] w-full max-w-md overflow-auto rounded-xl bg-white p-5 shadow-xl">
        {title && <h3 className="mb-3 text-base font-semibold text-slate-900">{title}</h3>}
        {children}
      </div>
    </div>
  );
}

// His `addName` (index.html:1085): a name typed is kept for the warehouse through his `enqueue` (956) —
// one write after another for that warehouse ("team:"+w) — and shown under Added at once, among the others
// in his order (localeCompare), before the server answers. His database keeps a write until it lands, so a
// request that never reached the server is sent again (the server keeps a name once, TeamName.add!); one
// still not kept is said with his `e.write` toast — the page's, so it shows even once the picker has closed
// at the tap and the person has gone back to the list — and the other lists show what the server has.
// Hook-level callbacks: they run even when the sheet that asked is gone.
function useKeepName(warehouseId: number | null, onFailed: (msg: string) => void) {
  const { t } = useT();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (n: string) => addFitterName(warehouseId!, n),
    scope: { id: `team-name-${warehouseId}` },
    // Sent even when the device says it is offline, never held back in silence (TanStack's default holds a
    // write until the browser reports a network): no network is a failure like any other, said below.
    networkMode: "always",
    // Twice more, as every read here (queryClient: a phone on a patchy signal); a refusal is not retried.
    retry: (n, e) => n < 2 && typeof (e as { status?: unknown }).status !== "number",
    onMutate: async (n) => {
      await qc.cancelQueries({ queryKey: ["fitters", warehouseId] });
      // A name the list already holds keeps its spelling there; a new one joins in his order.
      qc.setQueriesData<Fitters>({ queryKey: ["fitters", warehouseId] }, (old) => old && {
        ...old, added: old.added.some((x) => x.toLowerCase() === n.toLowerCase()) ? old.added : [...old.added, n].sort((a, b) => a.localeCompare(b)),
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["fitters", warehouseId] }),
    onError: (e) => { onFailed(failText(t, e, "job.writeFailed")); void qc.invalidateQueries({ queryKey: ["fitters", warehouseId] }); },
  });
}

// The server's spelling of a name it listed when last read (`known`: its crew and its added names) —
// only a spelling: that read can be up to 30 s old (staleTime), so whether the warehouse still keeps the
// name is never decided from it.
function serverHas(fitters: Fitters | undefined, n: string) {
  return (fitters?.known ?? []).find((x) => x.toLowerCase() === n.toLowerCase());
}

// His `exists` (addName) for the planning's own people: a crew member is the planning's, never kept by
// hand. Every other name typed is sent, even one the list showed — a name deleted on the server since
// that read would otherwise be skipped (MON-037, "ZZTEST R52 Bob") — and the server keeps it once
// whatever the case (TeamName.add!), so sending a name it has costs nothing.
function inCrew(fitters: Fitters | undefined, n: string) {
  return [...(fitters?.extras ?? []), ...(fitters?.fixes ?? [])].find((x) => x.toLowerCase() === n.toLowerCase());
}

function StartDialog({ warehouseId, eventId, heading, busy, initial = [], onCancel, onStart, onFailed, exclude = [], adding = false }: {
  warehouseId: number | null; eventId: number; heading: string; busy: boolean; onCancel: () => void; onStart: (fitters: string[]) => void;
  // A name typed here that could not be kept for the warehouse: said on the page.
  onFailed: (msg: string) => void;
  // "+ Fitter": the picker without the people already on the job. `initial`: a job that already has its
  // team (added before the start) opens with them picked.
  initial?: string[]; exclude?: string[]; adding?: boolean;
}) {
  const { t } = useT();
  // "on <client>" never names this very job.
  const fitters = useQuery({ queryKey: ["fitters", warehouseId, eventId], queryFn: () => fetchFitters(warehouseId, eventId) });
  const [picked, setPicked] = useState<string[]>(initial);
  const [typed, setTyped] = useState("");
  // His `addName`: trimmed, 40 characters at most, kept for this warehouse, and picked at once — still
  // picked for this start when it could not be kept.
  const save = useKeepName(warehouseId, onFailed);
  // His TEAM.set before the write: a name typed here stays under Added on this open picker, kept or not —
  // a re-read after a failed save does not take it away.
  const [typedHere, setTypedHere] = useState<string[]>([]);

  const toggle = (n: string) => setPicked((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));
  // Every name typed that is not a crew member is sent — the server keeps it once, or says why it cannot
  // (a job with no warehouse): never skipped on a list read before the server lost it (`inCrew`).
  const addTyped = () => {
    const n = typed.trim().replace(/\s+/g, " ").slice(0, 40);
    if (!n) return;
    const crewName = inCrew(fitters.data, n);
    const name = crewName ?? serverHas(fitters.data, n) ?? n;
    if (!crewName) { save.mutate(name); setTypedHere((h) => (h.some((x) => x.toLowerCase() === name.toLowerCase()) ? h : [...h, name])); }
    if (!picked.includes(name)) setPicked([...picked, name]);
    setTyped("");
  };

  const busyOn = fitters.data?.busy_on ?? {};
  const listed = new Set<string>();
  // His `staffOf`: the added names are the warehouse's and the ones typed here, never a crew member's, in
  // his order (localeCompare).
  const crew = new Set([...(fitters.data?.extras ?? []), ...(fitters.data?.fixes ?? [])].map((n) => n.toLowerCase()));
  const added = [...(fitters.data?.added ?? []), ...typedHere]
    .filter((n, i, all) => !crew.has(n.toLowerCase()) && all.findIndex((x) => x.toLowerCase() === n.toLowerCase()) === i)
    .sort((a, b) => a.localeCompare(b));
  // His three groups: Extras, Added, then the permanent staff.
  const groups = ([["start.extras", fitters.data?.extras], ["start.added", added], ["start.permanent", fitters.data?.fixes]] as const)
    .map(([label, names]) => [label, (names ?? []).filter((n) => !exclude.includes(n))] as const)
    .filter(([, names]) => names.length > 0);
  groups.forEach(([, names]) => names.forEach((n) => listed.add(n)));

  return (
    <Dialog title={adding ? t("start.addTitle") : t("start.title")} onClose={onCancel}>
      {heading && <p className="-mt-1 mb-2 font-semibold text-slate-800">{heading}</p>}
      <p className="mb-3 text-xs text-slate-500">{t("start.help")}</p>
      {fitters.data && groups.length === 0 && <p className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">{t("start.noStaff")}</p>}
      {groups.map(([label, names]) => (
        <div key={label} className="mb-3">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">{t(label)}</span>
          <div className="flex flex-wrap gap-2">
            {names.map((n) => (
              <button key={n} onClick={() => toggle(n)} aria-pressed={picked.includes(n)}
                className={`rounded-full px-3 py-1 text-left text-sm ${picked.includes(n) ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>
                {n}
                {busyOn[n] != null && <small className={`block text-xs ${picked.includes(n) ? "text-amber-200" : "text-amber-700"}`}>{t("start.busyOn", { c: busyOn[n].slice(0, 28) })}</small>}
              </button>
            ))}
          </div>
        </div>
      ))}
      {picked.some((n) => !listed.has(n)) && (
        <div className="mb-3 flex flex-wrap gap-2">
          {picked.filter((n) => !listed.has(n)).map((n) => (
            <button key={n} onClick={() => toggle(n)} className="rounded-full bg-slate-900 px-3 py-1 text-sm text-white">{n}</button>
          ))}
        </div>
      )}
      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500" htmlFor="new-fitter">{t("start.addName")}</label>
      <div className="mb-4 flex gap-2">
        <input id="new-fitter" value={typed} maxLength={40} autoComplete="off" onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTyped(); } }}
          className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <button onClick={addTyped} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">{t("start.add")}</button>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="rounded-lg px-4 py-2 text-sm text-slate-600">{t("cancel")}</button>
        <button onClick={() => onStart(picked)} disabled={picked.length === 0 || busy}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
          {picked.length === 0 ? t("start.atLeastOne")
            : t(adding ? (picked.length > 1 ? "start.addGoMany" : "start.addGoOne") : (picked.length > 1 ? "start.goMany" : "start.goOne"), { n: picked.length })}
        </button>
      </div>
    </Dialog>
  );
}

function IssueDialog({ items, preselect, heading, team, warehouseId, addressees, sending, error, onCancel, onReport, onFailed }: {
  items: Item[]; preselect: number | null; onCancel: () => void;
  // A name typed here that could not be kept for the warehouse: said on the page.
  onFailed: (msg: string) => void;
  // While the report goes out, Send waits (a double tap sends one); a refusal is said here.
  sending: boolean; error: string | null;
  // His sheet: the job named on top, who reports (the team, else the warehouse's people), and To / Cc.
  heading: string; team: string[]; warehouseId: number | null;
  addressees: Addressees | null;
  onReport: (v: { kind: string; description: string; item_id?: number | null; line_id?: number | null; affected_qty?: number | null; reported_by: string[]; blocks_assembly: boolean }) => void;
}) {
  const { t } = useT();
  const [kind, setKind] = useState<string>(ISSUE_KINDS[0]);
  // His "Signalé par": one person — from the team on the job, else the warehouse's people; picked
  // already when one fitter is on it. A name typed here is kept for the warehouse, as in the picker — and,
  // as his `addName` does, whether the warehouse has it is the warehouse's list, read even when the job's
  // team is shown.
  const fitters = useQuery({ queryKey: ["fitters", warehouseId], queryFn: () => fetchFitters(warehouseId) });
  const people = team.length ? team : [...(fitters.data?.extras ?? []), ...(fitters.data?.added ?? []), ...(fitters.data?.fixes ?? [])];
  const [by, setBy] = useState<string | null>(team.length === 1 ? team[0] : null);
  const [typed, setTyped] = useState("");
  const [missing, setMissing] = useState<"text" | "by" | null>(null);
  const save = useKeepName(warehouseId, onFailed);
  const addTyped = () => {
    const n = typed.trim().replace(/\s+/g, " ").slice(0, 40);
    if (!n) return;
    const crewName = inCrew(fitters.data, n);
    const kept = crewName ?? serverHas(fitters.data, n);
    if (!crewName) save.mutate(kept ?? n);
    setBy(people.find((x) => x.toLowerCase() === n.toLowerCase()) ?? kept ?? n);
    if (missing === "by") setMissing(null);
    setTyped("");
  };
  const [itemId, setItemId] = useState<number | null>(preselect);
  const [affected, setAffected] = useState(1);
  const chosen = items.find((i) => i.id === itemId) ?? null;
  const [description, setDescription] = useState("");
  const [blocks, setBlocks] = useState(false);

  return (
    <Dialog title={t("issue.title")} onClose={onCancel}>
      {heading.trim() && <p className="-mt-1 mb-3 font-semibold text-slate-800">{heading}</p>}
      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">{t("issue.kind")}</label>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {ISSUE_KINDS.map((k) => (
          <button key={k} onClick={() => setKind(k)}
            className={`rounded-full px-2.5 py-1 text-xs ${kind === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>
            {t(`issue.${k}`)}
          </button>
        ))}
      </div>

      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">{t("issue.item")}</label>
      <select value={itemId ?? ""} onChange={(e) => { setItemId(e.target.value ? Number(e.target.value) : null); setAffected(1); }}
        className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
        <option value="">{t("issue.none")}</option>
        {items.map((i) => (
          <option key={i.id} value={i.id}>
            {i.qty} × {i.name}{i.product_type_slug ? ` (${furnitureType(t, i.product_type_slug, i.qty)})` : ""}{i.order_id ? ` – ${t("job.order")} #${i.order_id}` : ""}
          </option>
        ))}
      </select>
      {/* His "Combien sont touchés ?" when the line has several pieces. */}
      {chosen && chosen.qty > 1 && (
        <label className="mb-3 flex items-center gap-2 text-sm text-slate-700">
          {t("issue.howMany")}
          <input type="number" min={1} max={chosen.qty} value={affected} inputMode="numeric"
            onChange={(e) => setAffected(Math.min(chosen.qty, Math.max(1, Number(e.target.value) || 1)))}
            className="w-20 rounded-lg border border-slate-300 px-2 py-1" />
          {t("issue.ofN").replace("{n}", String(chosen.qty))}
        </label>
      )}

      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">{t("issue.what")}</label>
      <textarea value={description} onChange={(e) => { setDescription(e.target.value); if (missing === "text" && e.target.value.trim()) setMissing(null); }} rows={3} maxLength={2000} placeholder={t("issue.placeholder")}
        className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />

      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">{t("issue.by")}</span>
      <div className="mb-2 flex flex-wrap gap-2">
        {[...people, ...(by && !people.includes(by) ? [by] : [])].map((n) => (
          <button key={n} onClick={() => { setBy(n); if (missing === "by") setMissing(null); }} aria-pressed={by === n}
            className={`rounded-full px-3 py-1 text-sm ${by === n ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>{n}</button>
        ))}
      </div>
      <div className="mb-3 flex gap-2">
        <input value={typed} maxLength={40} autoComplete="off" placeholder={t("start.addName")} onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTyped(); } }}
          className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <button onClick={addTyped} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">{t("start.add")}</button>
      </div>

      {/* ⛔ A blocking problem pauses the timer: a fitter waiting on a replacement part is not
          working, and counting that wait would make tomorrow's estimate a fiction. */}
      <label className="mb-4 flex items-start gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={blocks} onChange={(e) => setBlocks(e.target.checked)} className="mt-0.5" />
        {t("issue.blocks")}
      </label>

      {addressees && (
        <div className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          {/* "À :" in French, "To:" in the other three — the colon is in each language's string. */}
          <div><b>{t("issue.to")}</b> {addressees.to.join(", ")}</div>
          {addressees.cc.length > 0 && <div><b>{t("issue.cc")}</b> {addressees.cc.join(", ")}</div>}
          {addressees.rep_missing && <div className="mt-1 text-amber-700">{t("issue.unsales")}</div>}
        </div>
      )}
      {missing && <p className="mb-2 text-sm font-medium text-red-700">{t(missing === "text" ? "issue.needText" : "issue.needBy")}</p>}
      {error && <p role="alert" className="mb-2 text-sm font-medium text-red-700">{error}</p>}

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="rounded-lg px-4 py-2 text-sm text-slate-600">{t("cancel")}</button>
        <button
          onClick={() => {
            // His checks: describe it, and say who.
            if (!description.trim()) return setMissing("text");
            if (!by) return setMissing("by");
            onReport({
            // A row of Ludovic's list is an order line, not a delivery item.
            kind, description,
            ...(chosen?.kind === "order_line" ? { line_id: itemId } : { item_id: itemId }),
            affected_qty: chosen ? (chosen.qty > 1 ? affected : 1) : null,
            reported_by: [by],
            blocks_assembly: blocks,
            });
          }}
          disabled={sending}
          className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
          {/* His sendRep: L.sending while it goes out, then L.send(first name) again. */}
          {sending ? t("issue.sendingNow") : addressees ? t("issue.sendTo", { n: repFirst(addressees) }) : t("issue.send")}
        </button>
      </div>
    </Dialog>
  );
}

// His sales rep as his buttons name him (`a.owner`, the first of the contract's reps by name — the server's
// `IssueReport.owner`): in full, or by first name (his `first`) — "Ludo" when the job has none, never the
// name of the Ludovic it then goes to.
function repName(a: Addressees | null) {
  return a && !a.rep_missing ? a.to[0] || "Ludo" : "Ludo";
}
function repFirst(a: Addressees | null) {
  return repName(a).trim().split(/\s+/)[0] || "Ludo";
}

// His "Terminer le montage" (`openFinish` / `finGo`): what is done and what is not, the photos, the
// required packing tick, his two "tick everything", the notes (kept), whether the summary goes out,
// and to whom.
export function FinishDialog({ run, totals, checks, issues, heading, estimate, addressees, client, resendOnly, finished = false, error = null, busy, onCancel, onFinish }: {
  run: NonNullable<Delivery["run"]>;
  // His resend sheet (`openFinish(j, true)`): the summary, the note to edit, To / Cc, and Send.
  client: string; resendOnly: boolean; busy: boolean;
  // His SH.done: this sheet finished the job and its summary could not go out — the next tap only sends
  // (or, the send tick off, keeps the note), and `error` (his #sMsg) says why it stayed open.
  finished?: boolean; error?: string | null;
  totals: { total: number; toBuild: number; assembled: number; checked: number };
  checks: { done: number; total: number };
  issues: number;
  heading: string;
  estimate: number | null;
  addressees: Addressees | null;
  onCancel: () => void;
  onFinish: (v: { packing_confirmed: boolean; recap_notes?: string; send_recap: boolean; tick_all_assembled: boolean; tick_all_checked: boolean }) => void;
}) {
  const { t } = useT();
  const media = useQuery({ queryKey: ["media", "run", run.id], queryFn: () => fetchMedia("run", run.id) });
  const [packed, setPacked] = useState(false);
  const [needPack, setNeedPack] = useState(false);
  const [allAssembled, setAllAssembled] = useState(false);
  const [allChecked, setAllChecked] = useState(false);
  const [notes, setNotes] = useState(run.recap_notes ?? "");
  const [send, setSend] = useState(true);
  // His sheet's time line, read at the moment it opens (`times` and `perPerson` at that instant) from the
  // run's own segments and rounded once — the same count as the timer line behind it.
  const [openedAt] = useState(() => Date.now());
  const byPerson = perPersonMinutes(run, openedAt);
  const known = Object.keys(byPerson).length > 0;
  const worked = known ? Object.values(byPerson).reduce((a, b) => a + b, 0) : (run.worked_seconds ?? run.worked_minutes * 60) / 60;
  const left = totals.toBuild - totals.assembled;
  const chkLeft = checks.total - checks.done;
  const list = media.data?.media ?? [];
  const photos = list.filter((m) => m.kind === "image").length, videos = list.filter((m) => m.kind === "video").length;
  const counted = [photos && t(photos > 1 ? "fin.photosMany" : "fin.photosOne", { n: photos }), videos && t(videos > 1 ? "fin.videosMany" : "fin.videosOne", { n: videos })]
    .filter(Boolean).join(` ${t("fin.and")} `);
  // His finGo: the note as it stands — an emptied one goes as "" and clears it (d.note = noteTxt || null).
  // Once per tap that is taken, as his SH.done / SH.sending: a second tap before the button reads "Sending…"
  // sends nothing, so the thank-you is opened by the request that finished the job and queued its summary.
  // A refusal leaves the sheet open, and the next tap is taken again.
  const tapped = useRef(false);
  useEffect(() => { if (!busy) tapped.current = false; }, [busy]);
  const go = () => {
    if (tapped.current || busy) return;
    if (resendOnly || finished) { tapped.current = true; return onFinish({ packing_confirmed: true, recap_notes: notes.trim(), send_recap: resendOnly || send, tick_all_assembled: false, tick_all_checked: false }); }
    if (!packed) return setNeedPack(true);
    tapped.current = true;
    onFinish({ packing_confirmed: true, recap_notes: notes.trim(), send_recap: send, tick_all_assembled: allAssembled, tick_all_checked: allChecked });
  };

  return (
    <Dialog title={resendOnly ? t("finish.resend") : t("finish.title")} onClose={onCancel}>
      {heading.trim() && <p className="-mt-1 mb-3 font-semibold text-slate-800">{heading}</p>}
      {/* The dialog is a last-chance audit and is deliberately honest about what is unfinished. */}
      <div className="mb-3 space-y-1 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
        <div>{bold(t("fin.time"), { r: fmin(worked), e: fmin(estimate ?? 0) })}</div>
        <div className="text-xs text-slate-500">{Object.entries(known ? byPerson : run.minutes_by_person).map(([w, m]) => `${w} ${fmin(m)}`).join(" · ") || "—"}</div>
        {totals.toBuild > 0 && <div>{left > 0 ? `⚠️ ${t(left > 1 ? "fin.leftMany" : "fin.leftOne", { n: left })}` : `✅ ${t("fin.allOk")}`}</div>}
        {checks.total > 0 && <div>{chkLeft > 0 ? `⚠️ ${t(chkLeft > 1 ? "fin.chkLeftMany" : "fin.chkLeftOne", { n: chkLeft })}` : `✅ ${t("fin.chkAllOk")}`}</div>}
        <div>{issues > 0 ? `⚠️ ${t(issues > 1 ? "fin.probsMany" : "fin.probsOne", { n: issues })}` : t("fin.probsNone")}</div>
        <div className={counted ? "" : "font-medium text-red-700"}>{counted ? t("fin.media", { c: counted }) : t("fin.noMedia")}</div>
      </div>
      <div className="mb-3"><MediaStrip type="run" id={run.id} job itemKey={null} tabletLabel={`${client} — ${t("st.gen")}`} /></div>

      {!resendOnly && (
        <>
          <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
            <p className="text-xs font-semibold text-amber-900">⚠ {t("job.packing")}</p>
            <p className="mt-0.5 text-xs text-amber-800">{t("job.packingBody")}</p>
          </div>
          <label className="mb-2 flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={packed} onChange={(e) => { setPacked(e.target.checked); setNeedPack(false); }} className="mt-0.5" />
            {t("fin.pack")}
          </label>
        </>
      )}
      {left > 0 && !resendOnly && (
        <label className="mb-2 flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={allAssembled} onChange={(e) => setAllAssembled(e.target.checked)} className="mt-0.5" />{t("fin.all")}
        </label>
      )}
      {chkLeft > 0 && !resendOnly && (
        <label className="mb-2 flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={allChecked} onChange={(e) => setAllChecked(e.target.checked)} className="mt-0.5" />{t("fin.chkAll")}
        </label>
      )}

      <label className="mb-1 mt-2 block text-xs font-medium uppercase tracking-wide text-slate-500" htmlFor="fin-note">{t("fin.note")}</label>
      <textarea id="fin-note" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={1500} placeholder={t("fin.notePh")}
        className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />

      {!resendOnly && (
        <label className="mb-2 flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={send} onChange={(e) => setSend(e.target.checked)} className="mt-0.5" />
          {t("fin.send", { n: repName(addressees) })}
        </label>
      )}
      {addressees && (
        <div className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <div><b>{t("issue.to")}</b> {addressees.to.join(", ")}</div>
          {addressees.cc.length > 0 && <div><b>{t("issue.cc")}</b> {addressees.cc.join(", ")}</div>}
          {addressees.rep_missing && <div className="mt-1 text-amber-700">{t("issue.unsales")}</div>}
        </div>
      )}
      {needPack && <p className="mb-2 text-sm font-medium text-red-700">{t("fin.needPack")}</p>}
      {error && <p role="alert" className="mb-2 text-sm font-medium text-red-700">{error}</p>}

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="rounded-lg px-4 py-2 text-sm text-slate-600">{t("cancel")}</button>
        <button onClick={go} disabled={busy} className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
          {/* His resend sheet's button is his report's: L.send(first(a.owner) || "Ludo") — and so is his Finish
              sheet's once its send failed; his finGo reads L.sending while the summary goes out. */}
          {busy && (resendOnly || send) ? t("issue.sendingNow") : resendOnly || (finished && send) ? t("issue.sendTo", { n: repFirst(addressees) }) : t(send ? "fin.go" : "fin.goNo")}
        </button>
      </div>
    </Dialog>
  );
}

// His thank-you sheet after a finish (`openThanks` + `confetti`): the fitters by name, one of his three
// messages (picked by the event, so a job always gets the same one), what was done, where the summary
// went — and confetti, unless the device asks for less motion.
// `sentTo`: his `openThanks(id, j.a.owner||"Ludo")` — the sales rep this tap's summary went to, as the Finish
// button names him (`IssueReport.owner`, the first rep by name), Ludo when the job has none; "" when it went
// nowhere (his `openThanks(id, null)`).
export function ThankYou({ data, sentTo, client, eventId, onClose }: { data: Delivery; sentTo: string; client: string; eventId: number; onClose: () => void }) {
  const { t } = useT();
  const canvas = useRef<HTMLCanvasElement>(null);
  const run = data.run!;
  const names = Object.keys(run.minutes_by_person ?? {});
  const items = data.packs.flatMap((p) => p.items);
  const assembled = items.filter((i) => i.needs_assembly !== false).reduce((n, i) => n + Math.min(i.qty, i.assembled_qty), 0);
  const checked = items.filter((i) => i.needs_assembly === false).reduce((n, i) => n + Math.min(i.qty, i.checked_qty), 0);
  const hasBuild = items.some((i) => i.needs_assembly !== false), hasCheck = items.some((i) => i.needs_assembly === false);
  const who = names.length > 1 ? `${names.slice(0, -1).join(", ")} ${t("fin.and")} ${names[names.length - 1]}` : names[0];

  // His `confetti(cv)` as written: 90 pieces from the top centre (W/2 ± 40, H × 0.22), 5-10 px with a 0.6
  // aspect, spinning, gravity 0.22, in the theme's accent, good, warn and crit colours, fading out over
  // 2.8 s, then cleared. Nothing when the device asks for less motion.
  useEffect(() => {
    const cv = canvas.current;
    try { if (!cv || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return; } catch { return; }
    const box = cv.parentElement;
    const W = (cv.width = box?.clientWidth || 400), H = (cv.height = box?.clientHeight || 300);
    const g = cv.getContext("2d");
    if (!g) return;
    // His --accent, --good, --warn and --crit are our orange, emerald, amber and red (theme.css).
    const cs = getComputedStyle(document.documentElement);
    const cols = ["orange", "emerald", "amber", "red"].map((c) => {
      const v = cs.getPropertyValue(`--tw-c-${c}-600`).trim();
      return v ? `rgb(${v})` : "#c2562b";
    });
    const P = Array.from({ length: 90 }, () => ({
      x: W / 2 + (Math.random() - 0.5) * 80, y: H * 0.22, vx: (Math.random() - 0.5) * 10, vy: -Math.random() * 8 - 3,
      s: 5 + Math.random() * 5, r: Math.random() * 6, vr: (Math.random() - 0.5) * 0.3, c: cols[Math.floor(Math.random() * cols.length)],
    }));
    const t0 = performance.now();
    let raf = 0;
    const step = (tm: number) => {
      const e = tm - t0;
      g.clearRect(0, 0, W, H);
      for (const q of P) {
        q.vy += 0.22; q.x += q.vx; q.y += q.vy; q.r += q.vr;
        g.save(); g.translate(q.x, q.y); g.rotate(q.r); g.globalAlpha = Math.max(0, 1 - e / 2800);
        g.fillStyle = q.c; g.fillRect(-q.s / 2, -q.s / 2, q.s, q.s * 0.6); g.restore();
      }
      if (e < 2800 && cv.isConnected) raf = requestAnimationFrame(step); else g.clearRect(0, 0, W, H);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <Dialog title="" onClose={onClose}>
      <div className="relative flex flex-col items-center gap-3 py-2 text-center">
        <canvas ref={canvas} aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full" />
        <div className="text-5xl" aria-hidden="true">🙌</div>
        <h2 className="text-2xl font-bold text-slate-900">{names.length ? t("thx.title", { names: who }) : t("thx.titleTeam")}</h2>
        <p className="text-sm text-slate-700">{t("thx.lead", { c: client })}</p>
        <p className="max-w-[44ch] text-sm text-slate-600">{t(`thx.msg${Math.abs(eventId) % 3}`)}</p>
        <div className="flex flex-wrap justify-center gap-2 text-sm">
          {hasBuild && <span className="rounded-full bg-emerald-100 px-3 py-1"><b>{assembled}</b> {t(assembled > 1 ? "thx.asmMany" : "thx.asmOne")}</span>}
          {hasCheck && <span className="rounded-full bg-emerald-100 px-3 py-1"><b>{checked}</b> {t(checked > 1 ? "thx.chkMany" : "thx.chkOne")}</span>}
          {run.worked_minutes > 0 && <span className="rounded-full bg-emerald-100 px-3 py-1"><b>{fmin(run.worked_minutes)}</b> {t("thx.time")}</span>}
        </div>
        {sentTo && <p className="text-xs text-slate-500">{t("thx.sent", { n: sentTo })}</p>}
        <button onClick={onClose} autoFocus className="mt-1 rounded-lg bg-emerald-700 px-5 py-2 text-sm font-semibold text-white">{t("thx.close")}</button>
      </div>
    </Dialog>
  );
}

function Problem({ message }: { message: string }) {
  const { t } = useT();
  return (
    <div role="alert" className="mx-5 mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
      <b>{t("error.title")}:</b> {message}
    </div>
  );
}

// His "PRÉPARATION CLIENT" (5 h – 6 h on the preparation day): a tap shows the notes he put on the
// planning event — what to take out of stock, where it is, and what to do with it.
function ClientPrepCard({ prep }: { prep: ClientPreparation }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <button onClick={() => setOpen(!open)}
        className="w-full rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-left text-sm text-violet-900">
        <span className="font-mono text-xs text-violet-600">05:00</span> {prep.title}
      </button>
      {open && <pre className="mt-1 whitespace-pre-wrap rounded-lg bg-white px-3 py-2 text-xs text-slate-700 ring-1 ring-violet-100">{prep.notes}</pre>}
    </li>
  );
}

function ReceivedForJob({ eventId }: { eventId: number }) {
  const { t } = useT();
  const q = useQuery({ queryKey: ["reception-for-event", eventId], queryFn: () => fetchReceptionForEvent(eventId) });
  const receipts = q.data?.receipts ?? [];
  if (receipts.length === 0) return null;
  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white px-5 py-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{t("note.sec")}</h3>
      <div className="space-y-3">
        {receipts.map((r) => (
          <div key={r.id}>
            <div className="mb-1 text-sm text-slate-600"><ReceiptHead receipt={r} /></div>
            <ReceptionResultView result={r.result} compact />
          </div>
        ))}
      </div>
    </div>
  );
}

// His "📦 Client archivé" / "Archivage" box (Z Furniture `arch`).
function ArchiveBox({ job, onArchived }: { job: AssemblyJob; onArchived: (msg: string) => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  // His `on = isArch(j)`: a running job is never archived here, whatever it is filed by once it stops.
  const on = isArch(job);
  const toggle = useMutation({
    mutationFn: () => archiveJob(job.id, !on),
    // His toasts: "Client archived: it is in the Archived tab." / "<client> is back in the lists."
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["prep-jobs"] });
      // A sheet no list holds reads its archive state from its own answer (`d.job`).
      void qc.invalidateQueries({ queryKey: ["prep-event", job.id] });
      onArchived(on ? t("arch.undone", { c: job.client ?? "—" }) : t("arch.done"));
    },
  });
  const a = job.archive;
  const running = job.run?.status === "running";
  // While it runs, his box says only that: pause or finish before archiving. His dates are `ddmm`: the
  // day archived by hand on the warehouses' clock (the day the Archived list shows), the delivery day.
  const text = on
    ? (a.how === "manual" ? t("arch.pMan").replace("{d}", ddmm(planningDay(a.at ?? new Date().toISOString()))).replace("{w}", a.by ? ` ${t("arch.by")} ${a.by}` : "")
      : t("arch.pAuto").replace("{d}", ddmm(job.delivery_on)))
    : running ? t("arch.run")
    : a.how === "manual" ? t("arch.pBack")
    : job.delivery_on ? t("arch.pWill").replace("{d}", ddmm(job.delivery_on)) : t("arch.pNone");
  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white px-5 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <b className="text-sm text-slate-800">{on ? t("arch.boxOn") : t("arch.boxOff")}</b>
        <span className="text-xs text-slate-600">{text}</span>
        <button onClick={() => toggle.mutate()} disabled={toggle.isPending || running}
          className="ml-auto rounded-lg border border-slate-300 px-3 py-1 text-sm disabled:opacity-40">
          {on ? t("arch.un") : t("arch.ar")}
        </button>
      </div>
      {toggle.error && <p className="mt-1 text-xs text-red-700">{failText(t, toggle.error, "job.writeFailed")}</p>}
    </div>
  );
}

// His `guide` section above the job list: the fitters' video in the screen's language (French, else
// English), shown until hidden; hidden or shown is remembered on this device, as his `guideMin`.
const GUIDE_MIN_KEY = "guideMin";
// How long before its end a link is treated as dead: a read that takes its time still never hands Play a
// link S3 already refuses.
const LINK_MARGIN = 10 * 60 * 1000;
// How long a fresh link is waited for before the dead one may be tried again.
const RECOVER_WAIT = 15_000;
// How long a link whose read failed waits before the player's own waiting / stalled / error reads again.
const RETRY_AFTER = 10_000;
// When a signed S3 link was signed and when it stops working (ms, the server's clock), from its own
// X-Amz-Date and X-Amz-Expires; null when it does not say.
function linkTimes(url: string) {
  try {
    const u = new URL(url, window.location.href);
    const at = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(u.searchParams.get("X-Amz-Date") ?? "");
    const secs = Number(u.searchParams.get("X-Amz-Expires"));
    if (!at || !(secs > 0)) return null;
    const start = Date.UTC(+at[1], +at[2] - 1, +at[3], +at[4], +at[5], +at[6]);
    return { start, end: start + secs * 1000 };
  } catch { return null; }
}
// Whether the player holds the picture at `at`: a moment the browser merely moved to — the end a dead
// link's pending seek resolves to, the 0:00 of a reload — has no data under it.
function pictured(v: HTMLVideoElement, at: number) {
  for (let i = 0; i < v.buffered.length; i++) if (at >= v.buffered.start(i) && at <= v.buffered.end(i) + 0.25) return true;
  return false;
}
export function GuidePanel() {
  const { t, locale } = useT();
  const [min, setMinState] = useState(() => { try { return localStorage.getItem(GUIDE_MIN_KEY) === "1"; } catch { return false; } });
  const minNow = useRef(min);
  // His Hide (index.html:690-691) pauses the video and hides it with CSS (`.min`): Show finds it where it
  // was, never reloaded from its poster. Hidden, it stays paused: no Play is wanted any more — a player in
  // error fires no "pause" to say so — and a read on its way puts its link in paused.
  const setMin = (v: boolean) => {
    if (v) {
      player.current?.pause();
      wantsPlay.current = false;
      if (resume.current) resume.current = { ...resume.current, play: false };
    }
    minNow.current = v;
    setMinState(v);
    try { localStorage.setItem(GUIDE_MIN_KEY, v ? "1" : "0"); } catch { /* this tab only */ }
  };
  const lang = locale === "fr" ? "fr" : "en";
  // The links are signed for 6 h (his /_blob/ links never expire): read again every 5 h, even on a
  // tablet left on this page all day.
  const q = useQuery({ queryKey: ["guide", lang], queryFn: () => fetchGuide(lang), staleTime: 5 * 60 * 60 * 1000, refetchInterval: 5 * 60 * 60 * 1000,
    refetchIntervalInBackground: true, refetchOnWindowFocus: false, enabled: !min });
  const player = useRef<HTMLVideoElement>(null);
  const [shown, setShown] = useState<GuideVideo | null>(null);
  // Where a video someone had started picks up after its link is swapped, and whether it was playing.
  const resume = useRef<{ at: number; play: boolean } | null>(null);
  const wantsPlay = useRef(false);
  // How far this device's clock is from the server's, from the link last read (signed the moment it was
  // asked for, `readAt` on this clock): a link is judged on the server's clock, so a tablet set hours
  // ahead does not take every fresh link for a dead one and sign again at every look.
  const skew = useRef(0);
  // The place reached: the last moment the video really had a picture — never the 0:00 a dead link's
  // reload or an error falls back to — so a fresh link carries on from there, as his never-expiring one.
  const reached = useRef(0);
  // The moment last sought (the player's own `seeking`) that it has not stood on yet: a seek still on
  // its way, or one the browser resolved somewhere else — a dead link's pending seek falls to the end,
  // whether that end is loaded or not. His link never dies, so his video goes where it was sent.
  const aim = useRef<number | null>(null);
  // Whether the player stands away from the moment sought: that end is not the video over, nor a place
  // reached. A real end is one played to, or one sought.
  const astray = (v: HTMLVideoElement) => aim.current != null && Math.abs(v.currentTime - aim.current) > 1;
  // Where the video is for a fresh link: the place a link going in will resume at, else the moment sought,
  // else the moment with a picture, else the place reached.
  const pos = () => {
    const v = player.current;
    if (resume.current) return resume.current.at;
    if (aim.current != null) return aim.current;
    return v && !v.error && v.readyState >= v.HAVE_CURRENT_DATA && pictured(v, v.currentTime) ? v.currentTime : reached.current;
  };
  // The video shown now. A recovery read can outlive the screen that started it (answered once the other
  // language is shown): what it reads, and what it puts in, go by this one, never by the video shown then.
  const showing = useRef<GuideVideo | null>(null);
  const take = (g: GuideVideo, readAt: number, keep?: { at: number; play: boolean }) => {
    const signed = linkTimes(g.video_url)?.start;
    if (signed != null && readAt) skew.current = readAt - signed;
    const v = player.current;
    const other = showing.current?.language !== g.language;
    // His guideSync changes the source with the language: the other language's video starts paused, at
    // 0:00 — never at the moment, nor with the Play, of the language left, whoever asked for the read.
    resume.current = other ? null : keep ?? (v && pos() > 0 ? { at: pos(), play: wantsPlay.current } : null);
    aim.current = null;
    // Its own reads too: the once-a-minute limit of the language left does not hold its first one back.
    if (other) { reached.current = 0; wantsPlay.current = false; lastTry.current = 0; }
    showing.current = g;
    setShown(g);
  };
  // A fresh link replaces the one shown whenever the video is not playing (not started, paused, ended or
  // hidden), so an old link never outlives its 6 h — but a playing video is never swapped under the
  // person watching it. His guideSync changes the source with the language only. A picture frozen on a
  // link at its end or dead (refused, in error, its read failed or let go) is not playing: waiting for
  // data that never comes (below HAVE_FUTURE_DATA), or still wanted after its read let go — the fresh link
  // goes in at once, at the place reached, playing if Play is wanted: never the 45 s the browser takes to
  // give up on it.
  const recovering = useRef(false);
  // The link whose read is under way, or spent (failed, or let go): see `recover`.
  const failed = useRef<string | null>(null);
  // The other language goes in at once, even while a recovery read of the one left is out.
  useEffect(() => {
    const g = q.data;
    if (!g || g.video_url === shown?.video_url || (recovering.current && shown?.language === g.language)) return;
    const v = player.current;
    const same = !!v && !!shown && shown.language === g.language;
    const spent = same && failed.current === shown.video_url;
    if (same && (old() || !!v.error || spent)
      && (v.paused || v.ended || v.readyState < v.HAVE_FUTURE_DATA || (spent && wantsPlay.current))) take(g, q.dataUpdatedAt, { at: pos(), play: wantsPlay.current });
    else if (!same || v.paused || v.ended) take(g, q.dataUpdatedAt);
  }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps
  // A link that died, or is about to (a tablet asleep past it, a video paused for hours): read a fresh one
  // and carry on from the place reached — playing if Play is still wanted when it comes: a Pause tapped
  // meanwhile is the person's, the error's own "pause" is not (onPause). One read at a time, never
  // cancelled by a later one (they join it instead). A read that failed (the dead link came back) or let go
  // after RECOVER_WAIT leaves the link spent for RETRY_AFTER: the player's own waiting / stalled / error
  // inside it reads once it is over (`owed`), never more than once per event — a video that cannot play
  // never loops — while the person's Play or tap reads at once (`person`), and a link read meanwhile (the
  // network back, a later answer) goes in and plays. A link not at its end is read again once a minute at
  // most.
  const lastTry = useRef(0);
  const spentAt = useRef(0);
  const owed = useRef(false);
  // A picture left frozen on a spent link — still "playing", waiting for data that never comes — gets no
  // second "stalled" from the browser before its own error, ~45 s on: once RETRY_AFTER is over, it counts
  // as one, so the video carries on by itself as soon as a fresh link can be read. A player in error (a
  // dead link that never loaded, or one cut off mid-way) reads again the same way, Play wanted or not: its
  // own Play control is not reached while it is in error — Chrome's controls send the page no click, and
  // play() on it fires no "play" — so a fresh link goes in by itself, paused at the place reached, and that
  // Play then plays it with one tap. So does one at rest (paused, ended) on a link at its end: Chrome
  // raises no error while it is paused, and its next Play would meet the dead link. A player waiting for
  // data on a link that gave no trouble (neither spent nor at its end) is only loading: his reloads nothing.
  const nudge = useRef(0);
  const needsLink = (v: HTMLVideoElement) => !!v.error || (old() && (v.paused || v.ended))
    || (wantsPlay.current && !v.paused && !v.ended && v.readyState < v.HAVE_FUTURE_DATA && (old() || failed.current === showing.current?.video_url));
  // The next read of a player that still needs a link — never while it is hidden (Show arms it again).
  const arm = (ms: number) => {
    window.clearTimeout(nudge.current);
    nudge.current = window.setTimeout(() => {
      const v = player.current;
      if (!v || minNow.current) return;
      if (owed.current || needsLink(v)) void recover();
    }, Math.max(0, ms));
  };
  const recover = async (person = false) => {
    const cur = showing.current;
    if (!cur) return;
    // A read already out is joined — one for the link shown now (the language left's, still out once the
    // other is shown) leaves this one owed, read the moment it is over.
    if (recovering.current) { if (failed.current !== cur.video_url) owed.current = true; return; }
    const url = cur.video_url;
    if (failed.current === url) {
      if (!person && Date.now() - spentAt.current < RETRY_AFTER) { owed.current = true; arm(spentAt.current + RETRY_AFTER + 100 - Date.now()); return; }
    } else if (!person && !old() && Date.now() - lastTry.current < 60_000) {
      // Read again once a minute at most — a fresh link that fails at once too, when that minute is over.
      arm(lastTry.current + 60_000 + 100 - Date.now());
      return;
    }
    failed.current = url;
    lastTry.current = Date.now();
    recovering.current = true;
    owed.current = false;
    let timer = 0, fresh = false;
    try {
      const r = await Promise.race([q.refetch(), new Promise<null>((done) => { timer = window.setTimeout(() => done(null), RECOVER_WAIT); })]);
      // The read answers in the language on screen when it comes back. Once the screen has put the other
      // language in (paused at 0:00), the link it recovers is gone: its answer and its next read with it.
      const g = r?.data;
      if (g && g.video_url !== url && showing.current?.video_url === url) {
        fresh = true;
        take(g, r.dataUpdatedAt, { at: pos(), play: wantsPlay.current });
      }
    } finally { window.clearTimeout(timer); recovering.current = false; spentAt.current = Date.now(); }
    if (fresh) return;
    // The language changed meanwhile: the player shown now reads at once if it needs a link.
    arm(showing.current?.video_url === url ? RETRY_AFTER + 100 : 0);
  };
  useEffect(() => () => { window.clearTimeout(nudge.current); owed.current = false; }, [shown, min]);
  // Show: a player left in error, frozen or at rest on a dead link reads again — Hide stopped its reads.
  useEffect(() => { if (!min) arm(0); }, [min]); // eslint-disable-line react-hooks/exhaustive-deps
  // His links never expire; ours say when they do (X-Amz-Date + X-Amz-Expires). One close to its end is
  // replaced while the video is not playing, so Play is never handed a dead link and the poster — signed
  // with it — never turns black; checked again the moment a tablet that slept is seen. The link judged is
  // the one of the video shown now: a timer or a read started before the language changed never takes the
  // language left's dead link for the new one's.
  const old = () => { const end = showing.current ? linkTimes(showing.current.video_url)?.end : null; return end != null && Date.now() - skew.current > end - LINK_MARGIN; };
  useEffect(() => {
    const end = shown && !min ? linkTimes(shown.video_url)?.end : null;
    if (end == null) return;
    // A read on its way for a recovery is joined, never cancelled: cancelled, it would answer with the
    // dead link and the Play pressed would be lost. A player at rest goes through `recover`, so a read
    // that fails is tried again.
    const check = () => {
      const v = player.current;
      if (!old() || (v && !v.paused && !v.ended)) return;
      if (v) arm(0); else void q.refetch({ cancelRefetch: false });
    };
    const id = window.setTimeout(check, Math.max(0, end - LINK_MARGIN - (Date.now() - skew.current)));
    document.addEventListener("visibilitychange", check);
    return () => { window.clearTimeout(id); document.removeEventListener("visibilitychange", check); };
  }, [shown, min]); // eslint-disable-line react-hooks/exhaustive-deps
  // A swapped link that should play is started here: a player that loads nothing before Play (an iPad)
  // would otherwise never reach its metadata.
  useEffect(() => {
    const v = player.current;
    if (v && resume.current?.play && v.paused) void v.play().catch(() => {});
  }, [shown]);
  // Play, or a player waiting for data, on a link at its end: a fresh one at once, never a frozen picture
  // while the browser retries the dead one.
  const onDeadLink = () => { if (old()) void recover(); };
  // A player in error is not reached by Play: one whose dead link never loaded (MEDIA_ERR_SRC_NOT_SUPPORTED)
  // fires no "play" for a tap — the browser rejects its play() — and one cut off mid-way (MEDIA_ERR_NETWORK)
  // first reloads the dead link from 0:00. The tap itself is the person's Play: one tap reads a fresh link
  // and plays it from the place reached. Caught on the way down, before the player's own controls take it.
  const onTapInError = (e: React.MouseEvent<HTMLVideoElement>) => {
    if (!e.currentTarget.error) return;
    wantsPlay.current = true;
    void recover(true);
  };
  return (
    <section aria-label={t("guide.title")} className="mb-4 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <b className="text-sm text-slate-800">{t("guide.title")}</b>
        <span className="text-xs text-slate-500">{t("guide.sub")}</span>
        <button onClick={() => setMin(!min)} aria-expanded={!min}
          className="ml-auto rounded-lg px-3 py-1 text-xs text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50">
          {t(min ? "guide.show" : "guide.hide")}
        </button>
      </div>
      {shown && (
        <video ref={player} key={shown.language} controls playsInline preload="metadata" poster={shown.poster_url} src={shown.video_url}
          // The person's Play reads a spent link again at once.
          onPlay={() => { wantsPlay.current = true; if (old()) void recover(true); }}
          onClickCapture={onTapInError}
          onWaiting={onDeadLink} onStalled={onDeadLink}
          onSeeking={(e) => { aim.current = e.currentTarget.currentTime; }}
          onSeeked={(e) => { if (!astray(e.currentTarget)) aim.current = null; }}
          // Only a moment with a picture is a place reached: the 0:00 of a reload is not, nor the end a
          // pending seek fell to.
          onTimeUpdate={(e) => { const v = e.currentTarget; if (!v.error && !astray(v) && v.readyState >= v.HAVE_CURRENT_DATA && pictured(v, v.currentTime)) reached.current = v.currentTime; }}
          // The "pause" a media error brings is not the person's: the recovered link plays on. Nor is the one
          // the end brings, fired just before "ended" (`ended` already true) — a dead link's pending seek
          // resolved at the end: onEnded decides. A pause on a link at its end reads a fresh one, tried again
          // if that read fails.
          onPause={(e) => {
            if (e.currentTarget.error) return;
            if (!e.currentTarget.ended) wantsPlay.current = false;
            if (old()) void recover();
          }}
          // An end with no picture under it, or away from the moment sought, is a dead link's pending seek,
          // not the video over: Play stays wanted.
          onEnded={(e) => { const v = e.currentTarget; if (pictured(v, v.currentTime) && !astray(v)) wantsPlay.current = false; }}
          onError={() => void recover()}
          onLoadedMetadata={(e) => {
            const r = resume.current;
            resume.current = null;
            if (!r) return;
            // From 0:00 there is nothing to seek: a seek would put the first frame over his poster.
            if (r.at > 0) e.currentTarget.currentTime = r.at;
            reached.current = r.at;
            if (r.play) void e.currentTarget.play().catch(() => {});
          }}
          className={`mt-3 w-full max-w-3xl rounded-lg bg-black ${min ? "hidden" : ""}`} />
      )}
    </section>
  );
}

// His `maybeHtml`: ordered under a name close to the client's (step 2a bis), not linked to it for sure —
// always shown, to check with the sales rep, never counted in the assembly time.
// His "to check" box. With `since`, the lines that are not his case: ordered for this client but
// arrived before the feed shows its deliveries, so maybe delivered already — their own heading, as the
// reason differs (requested-by: Harish 2026-10-29, "you can answer this question better than me").
function MaybeBlock({ items, admin, since }: { items: MaybeItem[]; admin: string | null | undefined; since?: string | null }) {
  const { t, locale } = useT();
  if (!items.length) return null;
  const n = items.reduce((s, i) => s + (i.qty || 1), 0);
  const unseen = since !== undefined;
  const date = since ? `${since.slice(8, 10)}.${since.slice(5, 7)}.${since.slice(0, 4)}` : "—";
  return (
    <section className="rounded-lg border-l-4 border-amber-400 bg-amber-50 px-4 py-3">
      <h3 className="text-sm font-semibold text-amber-900">⚠️ {unseen ? t(n > 1 ? "maybe.unseenMany" : "maybe.unseenOne", { n })
        : t(n > 1 ? "maybe.titleMany" : "maybe.titleOne", { n })}</h3>
      <p className="mt-1 text-xs text-amber-800">{unseen ? t("maybe.unseenHint", { date }) : t("maybe.hint")}</p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-800">
        {items.map((i, k) => (
          // His row: "n× title · type · order #oid · supplier · reference «ref» · dd.mm".
          <li key={`${i.order_id}-${k}`}>
            <b>{i.qty || 1}×</b> {i.name || (i.product_type_slug ? furnitureType(t, i.product_type_slug, 1) : "")}
            <span className="text-slate-500">
              {i.product_type_slug ? ` · ${furnitureType(t, i.product_type_slug, i.qty || 1)}` : ""}
              {" · "}{t("job.order")}{" "}
              {admin ? <a href={`${admin}/catalogue-orders/orders/${i.order_id}`} target="_blank" rel="noopener noreferrer" className="text-sky-700 underline">#{i.order_id}</a> : `#${i.order_id}`}
              {i.supplier ? ` · ${i.supplier}` : ""}
              {` · ${t("maybe.ref")} ${quote(i.reference ?? "", locale)}`}
              {i.arrival_on ? ` · ${ddmm(i.arrival_on)}` : ""}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// A name quoted the way the reader's language does: « … » in French, „…" in German, "…" elsewhere.
function quote(text: string, locale: string) {
  return locale === "fr" ? `« ${text} »` : locale === "de" ? `„${text}“` : `“${text}”`;
}
